import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useDropzone } from 'react-dropzone';
import ProjectInfoPanel from '@/components/ProjectInfoPanel';
import QuoteWorkflow from '@/components/QuoteWorkflow';
import AssemblyExtractor from '@/components/AssemblyExtractor';
import AuditTrailView from '@/components/AuditTrailView';
import DrawingLocationPanel from '@/components/DrawingLocationPanel';
import AnnotationTool from '@/components/AnnotationTool';
import PdfViewer from '@/components/PdfViewer';
import SpecViewer from '@/components/SpecViewer';
import { AnalysisError, PageError } from '@/components/ErrorScenes';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import {
  ArrowLeft, Upload, FileText, Play, Loader2, CheckCircle2, AlertCircle,
  Layers, BarChart3, DollarSign, BookOpen, Clock, Download, Send,
  MessageSquare, ZoomIn, ZoomOut, RotateCw, StickyNote, Plus, Trash2, X,
  GitCompare, ArrowUp, ArrowDown, Minus, CirclePlus, Link2, MapPin, Calculator, Pencil, Calendar,
  Eye, Ruler, Ban, AlertTriangle, Copy, GripVertical, FileType, FileSpreadsheet
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function DrawingViewer({ projectId, files }) {
  const [selectedFile, setSelectedFile] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [pdfUrl, setPdfUrl] = useState('');
  const [annotateMode, setAnnotateMode] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    if (files?.length > 0 && !selectedFile) setSelectedFile(files[0]);
  }, [files, selectedFile]);

  useEffect(() => {
    if (selectedFile) {
      // Fetch a PDF token for iframe embedding
      fetch(`${API}/projects/${projectId}/files/${selectedFile.file_id}/pdf-token`, { credentials: 'include' })
        .then(r => r.json())
        .then(data => {
          if (data.token) {
            setPdfUrl(`${API}/projects/${projectId}/files/${selectedFile.file_id}/pdf?token=${data.token}`);
          }
        })
        .catch(() => {
          setPdfUrl(`${API}/projects/${projectId}/files/${selectedFile.file_id}/pdf`);
        });
    }
  }, [selectedFile, projectId]);

  return (
    <div className="flex flex-col h-full" data-testid="drawing-viewer">
      {/* File selector + toolbar */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-[#E2E8ED] bg-[#F7FAFB]">
        <div className="flex items-center gap-2 overflow-x-auto">
          {(files || []).map(f => (
            <button key={f.file_id} onClick={() => setSelectedFile(f)}
              className={`text-xs px-3 py-1 rounded-full whitespace-nowrap transition-colors ${selectedFile?.file_id === f.file_id ? 'bg-[#0D1830] text-white' : 'bg-white border border-[#E2E8ED] text-[#446E87] hover:border-[#1D4053]'}`}
              data-testid={`file-tab-${f.file_id}`}>
              {f.filename?.replace('.pdf', '')}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button onClick={() => setZoom(z => Math.max(0.3, z - 0.2))} className="p-1.5 rounded hover:bg-[#72A4BF]/8" data-testid="zoom-out"><ZoomOut className="w-4 h-4 text-[#446E87]" /></button>
          <span className="text-xs text-[#72A4BF] w-10 text-center">{(zoom * 100).toFixed(0)}%</span>
          <button onClick={() => setZoom(z => Math.min(3, z + 0.2))} className="p-1.5 rounded hover:bg-[#72A4BF]/8" data-testid="zoom-in"><ZoomIn className="w-4 h-4 text-[#446E87]" /></button>
          <button onClick={() => setZoom(1)} className="p-1.5 rounded hover:bg-[#72A4BF]/8" title="Reset zoom"><RotateCw className="w-3.5 h-3.5 text-[#446E87]" /></button>
          <div className="w-px h-4 bg-[#DDD] mx-1" />
          <button onClick={() => setAnnotateMode(!annotateMode)}
            className={`px-2 py-1 rounded-lg text-[10px] font-medium transition-colors ${annotateMode ? 'bg-[#1D4053] text-[#0D1830]' : 'bg-white border border-[#E2E8ED] text-[#446E87] hover:border-[#1D4053]'}`}
            data-testid="toggle-annotate-mode">
            <Pencil className="w-3 h-3 inline mr-1" />{annotateMode ? 'Annotating' : 'Annotate'}
          </button>
        </div>
      </div>

      {/* PDF viewer area with annotation overlay */}
      <div className="flex-1 overflow-auto bg-[#E8E8E6] relative" ref={containerRef}>
        {pdfUrl ? (
          <div style={{ transform: `scale(${zoom})`, transformOrigin: 'top left', transition: 'transform 0.2s' }} className="relative">
            <PdfViewer url={pdfUrl} initialPage={1} scale={1} className="min-h-[80vh]" />
            {/* Annotation overlay - always visible, interactive only in annotate mode */}
            {selectedFile && (
              <div className="absolute inset-0" style={{ pointerEvents: annotateMode ? 'auto' : 'none' }}>
                <AnnotationTool
                  projectId={projectId}
                  fileId={selectedFile.file_id}
                  pageNumber={1}
                  containerRef={containerRef}
                />
              </div>
            )}
          </div>
        ) : (
          <div className="flex items-center justify-center h-full text-[#72A4BF] text-sm">Select a drawing to view</div>
        )}
      </div>
    </div>
  );
}

function AIAssistant({ projectId, quoteId, onQuoteUpdate }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const chatEndRef = useRef(null);

  useEffect(() => {
    const fetchHistory = async () => {
      try {
        const res = await fetch(`${API}/ai-assistant/history/${projectId}`, { credentials: 'include' });
        if (res.ok) {
          const history = await res.json();
          const msgs = history.flatMap(h => {
            let aiText = h.ai_response?.response || '';
            if (h.ai_response?.has_action && aiText) {
              aiText = aiText.replace(/```(?:json)?\s*\n?[\s\S]*?\n?```/g, '').trim();
              if (!aiText) aiText = `${h.ai_response.items_added || h.ai_response.items?.length || 0} item(s) added to quote.`;
            }
            return [
              { role: 'user', text: h.user_message },
              { role: 'ai', text: aiText || 'Done', data: h.ai_response }
            ];
          });
          setMessages(msgs);
        }
      } catch (e) {}
    };
    fetchHistory();
  }, [projectId]);

  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const sendMessage = async () => {
    if (!input.trim() || sending) return;
    const msg = input;
    setInput('');
    setMessages(prev => [...prev, { role: 'user', text: msg }]);
    setSending(true);
    try {
      const res = await fetch(`${API}/ai-assistant`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ project_id: projectId, message: msg, quote_id: quoteId || '' })
      });
      if (res.ok) {
        const data = await res.json();
        // Clean up response text: strip JSON code blocks for action responses
        let displayText = data.response || '';
        if (data.has_action && displayText) {
          displayText = displayText.replace(/```(?:json)?\s*\n?[\s\S]*?\n?```/g, '').trim();
          if (!displayText) displayText = `${data.items_added || data.items?.length || 0} item(s) added to quote.`;
        }
        setMessages(prev => [...prev, { role: 'ai', text: displayText || 'Done', data }]);
        if (data.quote_updated && onQuoteUpdate) onQuoteUpdate();
      }
    } catch (e) { setMessages(prev => [...prev, { role: 'ai', text: 'Error communicating with AI' }]); }
    finally { setSending(false); }
  };

  const suggestions = [
    'Add all insulation products to the quote',
    'What divisions were identified?',
    'Add all drywall and gypsum components',
    'Show me the most expensive items',
    'Add all structural steel components',
  ];

  return (
    <div className="flex flex-col h-full" data-testid="ai-assistant">
      <div className="px-3 py-2 border-b border-[#E2E8ED] bg-[#F7FAFB]">
        <div className="flex items-center gap-2">
          <MessageSquare className="w-4 h-4 text-[#0D1830]" />
          <span className="text-xs font-semibold text-[#0D1830]">AI Assistant</span>
          {quoteId && <Badge className="bg-[#72A4BF]/10 text-[#0D1830] border-0 text-[10px]">Quote linked</Badge>}
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-3 space-y-3">
        {messages.length === 0 && (
          <div className="py-6 text-center">
            <MessageSquare className="w-8 h-8 text-[#1D4053]/30 mx-auto mb-3" />
            <p className="text-xs text-[#72A4BF] mb-3">Ask me to add items to your quote or ask about the project</p>
            <div className="space-y-1.5">
              {suggestions.map((s, i) => (
                <button key={i} onClick={() => { setInput(s); }} className="block w-full text-left text-xs px-3 py-1.5 rounded-lg bg-[#72A4BF]/10 border border-[#1D4053]/10 text-[#446E87] hover:bg-[#72A4BF]/10 transition-colors"
                  data-testid={`suggestion-${i}`}>
                  "{s}"
                </button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[85%] rounded-2xl px-3 py-2 text-xs ${
              m.role === 'user' ? 'bg-[#0D1830] text-white rounded-tr-sm' : 'bg-white border border-[#E2E8ED] text-[#0D1830] rounded-tl-sm'
            }`}>
              <p className="whitespace-pre-wrap">{m.text}</p>
              {m.data?.items?.length > 0 && (
                <div className="mt-2 pt-2 border-t border-[#E2E8ED]/30">
                  <p className="text-[10px] text-[#72A4BF] mb-1">{m.data.items.length} items {m.data.quote_updated ? 'added to quote' : 'found'}:</p>
                  {m.data.items.slice(0, 5).map((it, j) => (
                    <div key={j} className="text-[10px] text-[#446E87]">- {it.component_name} ({it.quantity} {it.unit})</div>
                  ))}
                  {m.data.items.length > 5 && <p className="text-[10px] text-[#72A4BF]/60">+{m.data.items.length - 5} more</p>}
                </div>
              )}
            </div>
          </div>
        ))}
        {sending && (
          <div className="flex justify-start">
            <div className="bg-white border border-[#E2E8ED] rounded-2xl rounded-tl-sm px-3 py-2">
              <Loader2 className="w-4 h-4 text-[#1D4053] animate-spin" />
            </div>
          </div>
        )}
        <div ref={chatEndRef} />
      </div>
      <div className="p-3 border-t border-[#E2E8ED] bg-white">
        <div className="flex items-center gap-2">
          <Input value={input} onChange={(e) => setInput(e.target.value)} placeholder='e.g., "Add all roof insulation products"'
            className="text-xs h-9 flex-1 rounded-full" onKeyDown={(e) => e.key === 'Enter' && sendMessage()} data-testid="ai-chat-input" />
          <Button size="sm" onClick={sendMessage} disabled={!input.trim() || sending} className="h-9 w-9 p-0 rounded-full gold-gradient hover:opacity-90" data-testid="ai-send-btn">
            <Send className="w-4 h-4 text-[#0D1830]" />
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * Inline-editable table cell. Click the displayed value → swap to an input;
 * Enter or blur saves; Escape cancels. Used for quote line items so users
 * can adjust quantity, unit cost, unit price, and component name without
 * leaving the quote view.
 */
function EditableCell({ value, type = 'text', display, onSave, className = '', testid }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value);
  // Sync local input when the underlying value changes (e.g., after a server refresh).
  useEffect(() => { setVal(value); }, [value]);
  const save = () => {
    const v = type === 'number' ? (parseFloat(val) || 0) : val;
    setEditing(false);
    if (v !== value) onSave(v);
  };
  if (editing) {
    return (
      <Input
        type={type === 'number' ? 'number' : 'text'}
        value={val}
        onChange={(e) => setVal(e.target.value)}
        onBlur={save}
        onKeyDown={(e) => {
          if (e.key === 'Enter') save();
          if (e.key === 'Escape') { setEditing(false); setVal(value); }
        }}
        autoFocus
        step={type === 'number' ? 'any' : undefined}
        className={`h-6 text-[10px] px-1 ${className}`}
        data-testid={testid}
      />
    );
  }
  return (
    <span
      className={`cursor-pointer hover:bg-[#72A4BF]/10 px-1 py-0.5 rounded transition-colors ${className}`}
      onClick={() => { setVal(value); setEditing(true); }}
      title="Click to edit"
      data-testid={testid}
    >
      {display ?? (value === '' || value === null || value === undefined ? '—' : value)}
    </span>
  );
}


function QuoteWorksheet({ projectId, quoteId, onQuoteIdChange }) {
  const [quotes, setQuotes] = useState([]);
  const [activeQuote, setActiveQuote] = useState(null);
  const [showCreateQuote, setShowCreateQuote] = useState(false);
  const [showEditHeader, setShowEditHeader] = useState(false);
  const [headerForm, setHeaderForm] = useState(null);
  const [recipients, setRecipients] = useState([]);
  const [showDuplicate, setShowDuplicate] = useState(false);
  const [duplicateRecipientId, setDuplicateRecipientId] = useState('');
  const [duplicating, setDuplicating] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchQuotes = useCallback(async () => {
    try {
      const res = await fetch(`${API}/quotes?project_id=${projectId}`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setQuotes(data);
        // Honor the quoteId prop if provided (e.g. from QuotesListPanel deep-link).
        // Otherwise default to the first quote.
        if (data.length > 0 && !activeQuote) {
          const initial = (quoteId && data.find(q => q.quote_id === quoteId)) || data[0];
          setActiveQuote(initial);
          onQuoteIdChange?.(initial.quote_id);
        }
      }
    } catch (e) {}
    finally { setLoading(false); }
  }, [projectId, activeQuote, onQuoteIdChange, quoteId]);

  useEffect(() => { fetchQuotes(); }, [fetchQuotes]);

  // If the quoteId prop changes after initial load (e.g. user clicks another
  // row in QuotesListPanel without unmounting), switch the active quote.
  useEffect(() => {
    if (!quoteId || !quotes.length) return;
    if (activeQuote?.quote_id === quoteId) return;
    const target = quotes.find(q => q.quote_id === quoteId);
    if (target) setActiveQuote(target);
  }, [quoteId, quotes, activeQuote?.quote_id]);

  // Expose refresh
  useEffect(() => {
    window._refreshQuote = fetchQuotes;
    return () => { delete window._refreshQuote; };
  }, [fetchQuotes]);

  // Fetch project recipients for the customer dropdown.
  const fetchRecipients = useCallback(async () => {
    try {
      const res = await fetch(`${API}/projects/${projectId}/recipients`, { credentials: 'include' });
      if (res.ok) setRecipients(await res.json());
    } catch (e) {}
  }, [projectId]);
  useEffect(() => { fetchRecipients(); }, [fetchRecipients]);

  const openEditHeader = () => {
    if (!activeQuote) return;
    setHeaderForm({
      quote_name: activeQuote.quote_name || '',
      recipient_id: activeQuote.recipient_id || '',
      customer_name: activeQuote.customer_name || '',
      customer_email: activeQuote.customer_email || '',
      valid_until: activeQuote.valid_until || '',
      terms_conditions: activeQuote.terms_conditions || '',
      notes: activeQuote.notes || '',
    });
    setShowEditHeader(true);
  };

  // Picking a recipient snapshots their name/email onto the form (hybrid model:
  // recipient_id stays as a live link for navigation, but name/email are
  // captured at this moment so the quote doesn't silently change later).
  const onPickRecipient = (rid) => {
    const r = recipients.find(x => x.recipient_id === rid);
    setHeaderForm(prev => ({
      ...prev,
      recipient_id: rid,
      customer_name: r ? (r.contact_name || r.company_name || '') : prev.customer_name,
      customer_email: r ? (r.email || '') : prev.customer_email,
    }));
  };

  const saveHeader = async () => {
    if (!activeQuote || !headerForm) return;
    try {
      const res = await fetch(`${API}/quotes/${activeQuote.quote_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(headerForm),
      });
      if (res.ok) {
        const updated = await res.json();
        setActiveQuote(updated);
        setQuotes(prev => prev.map(q => q.quote_id === updated.quote_id ? updated : q));
        setShowEditHeader(false);
      }
    } catch (e) {}
  };

  const openDuplicate = () => {
    if (!activeQuote) return;
    // Default to current recipient so a Confirm-with-no-change duplicates as-is.
    setDuplicateRecipientId(activeQuote.recipient_id || '');
    setShowDuplicate(true);
  };

  const duplicateQuote = async () => {
    if (!activeQuote) return;
    setDuplicating(true);
    try {
      // Only send recipient_id if user picked a different contact than current.
      const body = {};
      if (duplicateRecipientId && duplicateRecipientId !== (activeQuote.recipient_id || '')) {
        body.recipient_id = duplicateRecipientId;
      }
      const res = await fetch(`${API}/quotes/${activeQuote.quote_id}/duplicate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      });
      if (res.ok) {
        const dup = await res.json();
        setQuotes(prev => [dup, ...prev]);
        setActiveQuote(dup);
        onQuoteIdChange?.(dup.quote_id);
        setShowDuplicate(false);
        toast.success(`Duplicated as ${dup.quote_number || 'new quote'}`);
      } else {
        toast.error('Failed to duplicate quote');
      }
    } catch (e) {
      toast.error('Failed to duplicate quote');
    } finally {
      setDuplicating(false);
    }
  };

  const createQuote = async () => {
    // Quote name is no longer prompted; backend auto-assigns quote_number
    // (e.g. QUO007) and uses it as the quote_name when none is supplied.
    try {
      const res = await fetch(`${API}/quotes`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ project_id: projectId })
      });
      if (res.ok) {
        const q = await res.json();
        setQuotes(prev => [q, ...prev]);
        setActiveQuote(q);
        onQuoteIdChange?.(q.quote_id);
        setShowCreateQuote(false);
      }
    } catch (e) {}
  };

  const removeItem = async (qiId) => {
    if (!activeQuote) return;
    try {
      const res = await fetch(`${API}/quotes/${activeQuote.quote_id}/items/${qiId}`, { method: 'DELETE', credentials: 'include' });
      if (res.ok) { const updated = await res.json(); setActiveQuote(updated); }
    } catch (e) {}
  };

  // Drag-and-drop reordering of quote line items.
  // Native HTML5 DnD — no library dependency.
  const [draggedItemId, setDraggedItemId] = useState(null);
  const [dragOverItemId, setDragOverItemId] = useState(null);

  const reorderItems = async (newOrder) => {
    if (!activeQuote) return;
    // Optimistic update: reorder client-side first, then PUT.
    const id_to_item = Object.fromEntries((activeQuote.items || []).map(i => [i.qi_id, i]));
    const reordered = newOrder.map(id => id_to_item[id]).filter(Boolean);
    setActiveQuote({ ...activeQuote, items: reordered });
    try {
      const res = await fetch(`${API}/quotes/${activeQuote.quote_id}/reorder`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ order: newOrder }),
      });
      if (res.ok) {
        const updated = await res.json();
        setActiveQuote(updated);
        setQuotes(prev => prev.map(q => q.quote_id === updated.quote_id ? updated : q));
      } else {
        toast.error('Failed to save new order');
      }
    } catch (e) {
      toast.error('Failed to save new order');
    }
  };

  const handleDrop = (targetQiId) => {
    if (!draggedItemId || !targetQiId || draggedItemId === targetQiId) return;
    const ids = (activeQuote.items || []).map(i => i.qi_id);
    const fromIdx = ids.indexOf(draggedItemId);
    const toIdx = ids.indexOf(targetQiId);
    if (fromIdx === -1 || toIdx === -1) return;
    const newOrder = [...ids];
    newOrder.splice(fromIdx, 1);
    newOrder.splice(toIdx, 0, draggedItemId);
    setDraggedItemId(null);
    setDragOverItemId(null);
    reorderItems(newOrder);
  };

  // Edit a single field on a quote item. Backend recomputes total_cost,
  // total_price, and margin from the new value, plus the quote-level totals.
  const updateItem = async (qiId, field, value) => {
    if (!activeQuote) return;
    try {
      const res = await fetch(`${API}/quotes/${activeQuote.quote_id}/items/${qiId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ [field]: value }),
      });
      if (res.ok) {
        const updated = await res.json();
        setActiveQuote(updated);
        setQuotes(prev => prev.map(q => q.quote_id === updated.quote_id ? updated : q));
      } else {
        toast.error('Failed to update item');
      }
    } catch (e) {
      toast.error('Failed to update item');
    }
  };

  // Lifecycle steps for the visual stepper (Pass — Option B).
  // Linear path: Draft → Sent → Viewed; then Won/Lost are the two terminal forks.
  // Backend statuses map onto these: draft/sent/viewed/accepted/rejected.
  // (revised stays as a backend value but isn't on the main path — shown as a
  // small badge if the quote has been revised.)
  const QUOTE_STAGE_PATH = [
    { id: 'draft',  label: 'Draft' },
    { id: 'sent',   label: 'Sent' },
    { id: 'viewed', label: 'Viewed' },
  ];
  const QUOTE_STAGE_TERMINALS = [
    { id: 'accepted', label: 'Won',  activeClass: 'bg-emerald-600 text-white border-emerald-600', hoverClass: 'hover:border-emerald-500 hover:text-emerald-700' },
    { id: 'rejected', label: 'Lost', activeClass: 'bg-red-500 text-white border-red-500',         hoverClass: 'hover:border-red-400 hover:text-red-600' },
  ];

  // Returns 'past' | 'current' | 'future' for a step in the linear path.
  // Anything past Viewed (accepted/rejected) makes the linear path all 'past'.
  const stepStateFor = (stepId, currentStatus) => {
    const order = ['draft', 'sent', 'viewed'];
    const stepIdx = order.indexOf(stepId);
    if (currentStatus === stepId) return 'current';
    if (currentStatus === 'accepted' || currentStatus === 'rejected') return 'past';
    const curIdx = order.indexOf(currentStatus || 'draft');
    if (curIdx > stepIdx) return 'past';
    return 'future';
  };

  const setQuoteStatus = async (q, newStatus) => {
    if (!q || q.status === newStatus) return;
    try {
      const res = await fetch(`${API}/quotes/${q.quote_id}/status-override`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status: newStatus }),
      });
      if (res.ok) {
        const updated = await res.json();
        setQuotes(prev => prev.map(x => x.quote_id === updated.quote_id ? updated : x));
        if (activeQuote?.quote_id === updated.quote_id) setActiveQuote(updated);
      } else {
        toast.error('Failed to update quote status');
      }
    } catch (e) {
      toast.error('Failed to update quote status');
    }
  };

  // Download the active quote as PDF / Word / Excel. PDF + Excel use the new
  // Nivela v5 template; Word stays on the legacy template until that design ships.
  const [exporting, setExporting] = useState(null);
  const exportQuote = async (fmt) => {
    if (!activeQuote) return;
    setExporting(fmt);
    try {
      const useV5 = fmt === 'pdf' || fmt === 'excel';
      const templateParam = useV5 ? '&template=v5' : '';
      const url = `${API}/quotes/${activeQuote.quote_id}/export/${fmt}?style=professional${templateParam}`;
      const res = await fetch(url, { credentials: 'include' });
      if (!res.ok) { toast.error('Export failed'); return; }
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      const ext = fmt === 'excel' ? 'xlsx' : fmt;
      a.download = `${activeQuote.quote_name || activeQuote.quote_number || 'quote'}.${ext}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(objectUrl);
    } catch (e) {
      toast.error('Export failed');
    } finally {
      setExporting(null);
    }
  };

  // Email the quote to the customer (and mark it Sent). Opens a dialog
  // pre-filled with recipient / subject / body so the user can edit before
  // sending. POST /quotes/{id}/send accepts all three as optional overrides.
  const [sendingQuote, setSendingQuote] = useState(false);
  const [showSendDialog, setShowSendDialog] = useState(false);
  const [sendForm, setSendForm] = useState({ to_email: '', subject: '', message: '' });

  const openSendDialog = () => {
    if (!activeQuote) return;
    const label = activeQuote.quote_name || activeQuote.quote_number || 'Your Quote';
    const customerName = activeQuote.customer_name || 'there';
    const total = (activeQuote.total_price || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    setSendForm({
      to_email: activeQuote.customer_email || '',
      subject: `Quote: ${label}`,
      message:
        `Hi ${customerName},\n\n` +
        `Please find your quote (${label}, total $${total}) ready for review. ` +
        `Click "View Quote" in this email to open the full document online.\n\n` +
        `Let me know if you have any questions.\n\n` +
        `Thanks`,
    });
    setShowSendDialog(true);
  };

  const sendQuoteEmail = async () => {
    if (!activeQuote) return;
    setSendingQuote(true);
    try {
      const res = await fetch(`${API}/quotes/${activeQuote.quote_id}/send`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to_email: sendForm.to_email,
          subject: sendForm.subject,
          message: sendForm.message,
        }),
      });
      if (res.ok) {
        const updated = await res.json();
        setQuotes(prev => prev.map(x => x.quote_id === updated.quote_id ? updated : x));
        // Refresh activeQuote from the server (the route doesn't return the full doc).
        const refreshed = await fetch(`${API}/quotes?project_id=${projectId}`, { credentials: 'include' });
        if (refreshed.ok) {
          const list = await refreshed.json();
          const next = list.find(q => q.quote_id === activeQuote.quote_id);
          if (next) setActiveQuote(next);
          setQuotes(list);
        }
        toast.success(updated.email_sent ? `Quote emailed to ${sendForm.to_email}` : 'Quote marked as sent (email skipped — no provider configured or no recipient)');
        setShowSendDialog(false);
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to send quote');
      }
    } catch (e) {
      toast.error('Failed to send quote');
    } finally {
      setSendingQuote(false);
    }
  };

  const selectQuote = (q) => {
    setActiveQuote(q);
    onQuoteIdChange?.(q.quote_id);
  };

  // 2 decimals on the quote view: line items (Unit Cost / Unit Price / Total)
  // and the header summary cards (Cost / Price).
  const fmtCurrency = (v) => `$${(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <div className="flex flex-col h-full" data-testid="quote-worksheet">
      <div className="px-3 py-2 border-b border-[#E2E8ED] bg-[#F7FAFB] flex items-center justify-between">
        <div className="flex items-center gap-2 overflow-x-auto">
          <FileText className="w-4 h-4 text-[#0D1830] shrink-0" />
          {quotes.map(q => (
            <button key={q.quote_id} onClick={() => selectQuote(q)}
              className={`text-xs px-2.5 py-1 rounded-full whitespace-nowrap transition-colors ${activeQuote?.quote_id === q.quote_id ? 'bg-[#0D1830] text-white' : 'bg-white border border-[#E2E8ED] text-[#446E87]'}`}
              data-testid={`quote-tab-${q.quote_id}`}>
              {q.quote_number || q.quote_name || 'Quote'}
            </button>
          ))}
        </div>
        <Button size="sm" variant="ghost" onClick={() => setShowCreateQuote(true)} className="h-7 text-xs text-[#0D1830]" data-testid="new-quote-btn">
          <Plus className="w-3 h-3 mr-1" />Quote
        </Button>
      </div>

      {loading ? (
        <div className="flex-1 flex items-center justify-center"><Loader2 className="w-5 h-5 text-[#1D4053] animate-spin" /></div>
      ) : !activeQuote ? (
        <div className="flex-1 flex items-center justify-center p-4 text-center">
          <div>
            <FileText className="w-8 h-8 text-[#1D4053]/30 mx-auto mb-3" />
            <p className="text-xs text-[#72A4BF] mb-3">Create a quote to start building your worksheet</p>
            <Button size="sm" onClick={() => setShowCreateQuote(true)} className="bg-[#0D1830] text-white rounded-full text-xs hover:opacity-90" data-testid="create-first-quote-btn">
              <Plus className="w-3 h-3 mr-1" />Create Quote
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex-1 overflow-auto">
          {/* Quote header */}
          <div className="px-3 py-2.5 border-b border-[#E2E8ED] bg-[#F7FAFB]/40">
            <div className="flex items-start justify-between gap-2 mb-2">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-bold text-[#0D1830] truncate" data-testid="quote-header-name">
                    {activeQuote.quote_number || activeQuote.quote_name || 'Quote'}
                  </p>
                  {/* If a custom name exists and differs from the number, surface it as a smaller subtitle */}
                  {activeQuote.quote_name && activeQuote.quote_number && activeQuote.quote_name !== activeQuote.quote_number && (
                    <span className="text-[10px] text-[#72A4BF] truncate">— {activeQuote.quote_name}</span>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 mt-1.5 text-[10px]">
                  <div>
                    <span className="text-[#72A4BF]">Customer: </span>
                    <span className="text-[#0D1830] font-medium">{activeQuote.customer_name || '—'}</span>
                  </div>
                  <div>
                    <span className="text-[#72A4BF]">Email: </span>
                    <span className="text-[#0D1830] font-medium truncate">{activeQuote.customer_email || '—'}</span>
                  </div>
                  <div>
                    <span className="text-[#72A4BF]">Valid until: </span>
                    <span className="text-[#0D1830] font-medium">{activeQuote.valid_until || '—'}</span>
                  </div>
                  <div className="col-span-2 flex items-center gap-2 mt-1.5" data-testid="quote-stage-stepper">
                    {/* Linear progression: Draft → Sent → Viewed */}
                    {QUOTE_STAGE_PATH.map((step, idx) => {
                      const state = stepStateFor(step.id, activeQuote.status);
                      const baseDot = 'w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold transition-colors';
                      const dotCls =
                        state === 'current'
                          ? 'bg-[#0D1830] text-white ring-2 ring-[#0D1830]/20'
                          : state === 'past'
                            ? 'bg-[#446E87] text-white'
                            : 'bg-white border border-[#E2E8ED] text-[#72A4BF]';
                      return (
                        <div key={step.id} className="flex items-center gap-1.5">
                          <button
                            type="button"
                            onClick={() => setQuoteStatus(activeQuote, step.id)}
                            className="group flex items-center gap-1.5"
                            data-testid={`stage-step-${step.id}`}
                            title={`Mark as ${step.label}`}
                          >
                            <span className={`${baseDot} ${dotCls} group-hover:ring-2 group-hover:ring-[#0D1830]/15`}>
                              {state === 'past' ? '✓' : idx + 1}
                            </span>
                            <span className={`text-[10px] font-semibold ${state === 'current' ? 'text-[#0D1830]' : state === 'past' ? 'text-[#446E87]' : 'text-[#72A4BF]'}`}>
                              {step.label}
                            </span>
                          </button>
                          {/* Connector line between steps */}
                          <span className={`h-px w-4 ${state === 'past' ? 'bg-[#446E87]' : 'bg-[#E2E8ED]'}`} />
                        </div>
                      );
                    })}
                    {/* Terminal forks: Won / Lost */}
                    {QUOTE_STAGE_TERMINALS.map(term => {
                      const isActive = activeQuote.status === term.id;
                      return (
                        <button
                          key={term.id}
                          type="button"
                          onClick={() => setQuoteStatus(activeQuote, term.id)}
                          className={`px-2.5 py-0.5 rounded-full border text-[10px] font-bold transition-colors
                            ${isActive ? term.activeClass : `bg-white border-[#E2E8ED] text-[#72A4BF] ${term.hoverClass}`}`}
                          data-testid={`stage-step-${term.id}`}
                          title={`Mark as ${term.label}`}
                        >
                          {term.label}
                        </button>
                      );
                    })}
                    {activeQuote.status === 'revised' && (
                      <span
                        className="px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider bg-amber-100 text-amber-700 border border-amber-200"
                        data-testid="stage-revised-badge"
                        title="This quote has been revised"
                      >
                        Revised v{activeQuote.version || 2}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                {/* Export pill group: PDF / Excel / Word. PDF + Excel use v5 template; Word is legacy. */}
                <div className="flex items-center border border-[#E2E8ED] rounded-full overflow-hidden bg-white">
                  <button
                    type="button"
                    onClick={() => exportQuote('pdf')}
                    disabled={!!exporting}
                    className="px-2.5 py-1 text-[10px] font-medium text-[#446E87] hover:bg-[#72A4BF]/10 flex items-center gap-1 border-r border-[#E2E8ED] disabled:opacity-50"
                    data-testid="export-pdf-btn"
                    title="Download as PDF"
                  >
                    {exporting === 'pdf' ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileType className="w-3 h-3 text-red-500" />}PDF
                  </button>
                  <button
                    type="button"
                    onClick={() => exportQuote('excel')}
                    disabled={!!exporting}
                    className="px-2.5 py-1 text-[10px] font-medium text-[#446E87] hover:bg-[#72A4BF]/10 flex items-center gap-1 border-r border-[#E2E8ED] disabled:opacity-50"
                    data-testid="export-excel-btn"
                    title="Download as Excel"
                  >
                    {exporting === 'excel' ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileSpreadsheet className="w-3 h-3 text-emerald-600" />}Excel
                  </button>
                  <button
                    type="button"
                    onClick={() => exportQuote('docx')}
                    disabled={!!exporting}
                    className="px-2.5 py-1 text-[10px] font-medium text-[#446E87] hover:bg-[#72A4BF]/10 flex items-center gap-1 disabled:opacity-50"
                    data-testid="export-docx-btn"
                    title="Download as Word"
                  >
                    {exporting === 'docx' ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileText className="w-3 h-3 text-blue-500" />}Word
                  </button>
                </div>
                {/* Send by email: visible only on draft (matches backend status-transition guard). */}
                {activeQuote.status === 'draft' && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={openSendDialog}
                    disabled={sendingQuote}
                    className="rounded-full text-[10px] border-[#1D4053] text-[#0D1830] hover:bg-[#72A4BF]/10"
                    data-testid="send-quote-btn"
                    title={activeQuote.customer_email ? `Compose and send to ${activeQuote.customer_email}` : 'Compose email and send'}
                  >
                    {sendingQuote ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Send className="w-3 h-3 mr-1" />}Send
                  </Button>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={openEditHeader}
                  className="rounded-full text-[10px]"
                  data-testid="edit-quote-header-btn"
                >
                  <Pencil className="w-3 h-3 mr-1" />Edit
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={openDuplicate}
                  className="rounded-full text-[10px]"
                  data-testid="duplicate-quote-btn"
                  title="Duplicate this quote (optionally to a different recipient)"
                >
                  <Copy className="w-3 h-3 mr-1" />Duplicate
                </Button>
              </div>
            </div>
          </div>
          {/* Quote summary */}
          <div className="grid grid-cols-3 gap-2 p-3 border-b border-[#E2E8ED]">
            <div className="text-center">
              <p className="text-[10px] text-[#72A4BF]">Cost</p>
              <p className="text-xs font-semibold">{fmtCurrency(activeQuote.total_cost)}</p>
            </div>
            <div className="text-center">
              <p className="text-[10px] text-[#72A4BF]">Price</p>
              <p className="text-xs font-semibold gold-text">{fmtCurrency(activeQuote.total_price)}</p>
            </div>
            <div className="text-center">
              <p className="text-[10px] text-[#72A4BF]">Margin</p>
              <p className={`text-xs font-semibold ${activeQuote.total_margin > 15 ? 'text-emerald-600' : 'text-[#0D1830]'}`}>{(activeQuote.total_margin || 0).toFixed(1)}%</p>
            </div>
          </div>
          {/* Items table */}
          {(activeQuote.items || []).length === 0 ? (
            <div className="p-6 text-center text-xs text-[#72A4BF]">
              No items yet. Use the AI Assistant to add items:<br />
              <em>"Add all insulation products"</em>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-[#E2E8ED]">
                  <TableHead className="text-[10px] w-6"></TableHead>
                  <TableHead className="text-[10px]">Component</TableHead>
                  <TableHead className="text-[10px] text-right">Qty</TableHead>
                  <TableHead className="text-[10px] text-right">Unit Cost</TableHead>
                  <TableHead className="text-[10px] text-right">Unit Price</TableHead>
                  <TableHead className="text-[10px] text-right">Total</TableHead>
                  <TableHead className="text-[10px] text-right">Margin</TableHead>
                  <TableHead className="text-[10px] w-8"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(activeQuote.items || []).map((item) => {
                  // Bidirectional margin/price math.
                  // Editing margin → derive new price from current cost, then PUT
                  // price to the backend (which recomputes total_price + margin).
                  const onMarginEdit = (newMarginPct) => {
                    const cost = Number(item.cost || 0);
                    const m = Number(newMarginPct);
                    if (!Number.isFinite(m)) return;
                    if (m >= 100) {
                      toast.error('Margin must be less than 100%');
                      return;
                    }
                    if (cost <= 0) {
                      toast.error('Set a unit cost first to derive price from margin');
                      return;
                    }
                    const newPrice = cost / (1 - m / 100);
                    updateItem(item.qi_id, 'price', Math.round(newPrice * 100) / 100);
                  };
                  const isDragging = draggedItemId === item.qi_id;
                  const isDragOver = dragOverItemId === item.qi_id && !isDragging;
                  return (
                    <TableRow
                      key={item.qi_id}
                      className={`border-[#F0F4F7] transition-colors ${isDragging ? 'opacity-40' : ''} ${isDragOver ? 'bg-[#0D1830]/5' : ''}`}
                      draggable={!!draggedItemId === false ? undefined : undefined}
                      onDragOver={(e) => {
                        if (draggedItemId && draggedItemId !== item.qi_id) {
                          e.preventDefault();
                          if (dragOverItemId !== item.qi_id) setDragOverItemId(item.qi_id);
                        }
                      }}
                      onDragLeave={() => {
                        if (dragOverItemId === item.qi_id) setDragOverItemId(null);
                      }}
                      onDrop={(e) => {
                        e.preventDefault();
                        handleDrop(item.qi_id);
                      }}
                    >
                      <TableCell className="text-[10px] w-6 px-1">
                        <span
                          draggable
                          onDragStart={(e) => {
                            setDraggedItemId(item.qi_id);
                            e.dataTransfer.effectAllowed = 'move';
                          }}
                          onDragEnd={() => {
                            setDraggedItemId(null);
                            setDragOverItemId(null);
                          }}
                          className="cursor-grab active:cursor-grabbing inline-flex p-0.5 text-[#72A4BF] hover:text-[#0D1830] hover:bg-[#F7FAFB] rounded"
                          title="Drag to reorder"
                          data-testid={`item-drag-${item.qi_id}`}
                        >
                          <GripVertical className="w-3 h-3" />
                        </span>
                      </TableCell>
                      <TableCell className="text-[10px] font-medium max-w-[150px]">
                        <EditableCell
                          value={item.component_name}
                          onSave={(v) => updateItem(item.qi_id, 'component_name', v)}
                          testid={`item-name-${item.qi_id}`}
                          className="block truncate"
                        />
                        {item.confidence > 0 && (
                          <div className="flex items-center gap-1 mt-0.5">
                            <div className="w-8 h-1 rounded-full bg-[#EEE] overflow-hidden">
                              <div className={`h-full rounded-full ${item.confidence >= 0.8 ? 'bg-emerald-500' : item.confidence >= 0.5 ? 'bg-[#1D4053]' : 'bg-red-400'}`}
                                style={{ width: `${item.confidence * 100}%` }} />
                            </div>
                            <span className="text-[8px] text-[#72A4BF]/60">{(item.confidence * 100).toFixed(0)}%</span>
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-[10px] text-right">
                        <EditableCell
                          value={item.quantity}
                          type="number"
                          onSave={(v) => updateItem(item.qi_id, 'quantity', v)}
                          testid={`item-qty-${item.qi_id}`}
                        />
                        {' '}
                        <EditableCell
                          value={item.unit || 'EA'}
                          onSave={(v) => updateItem(item.qi_id, 'unit', v)}
                          testid={`item-unit-${item.qi_id}`}
                        />
                      </TableCell>
                      <TableCell className="text-[10px] text-right">
                        <EditableCell
                          value={item.cost}
                          type="number"
                          display={fmtCurrency(item.cost)}
                          onSave={(v) => updateItem(item.qi_id, 'cost', v)}
                          testid={`item-cost-${item.qi_id}`}
                        />
                      </TableCell>
                      <TableCell className="text-[10px] text-right font-medium">
                        <EditableCell
                          value={item.price}
                          type="number"
                          display={fmtCurrency(item.price)}
                          onSave={(v) => updateItem(item.qi_id, 'price', v)}
                          testid={`item-price-${item.qi_id}`}
                        />
                      </TableCell>
                      <TableCell className="text-[10px] text-right font-semibold tabular-nums" data-testid={`item-total-${item.qi_id}`}>
                        {fmtCurrency(item.total_price)}
                      </TableCell>
                      <TableCell className="text-[10px] text-right">
                        <EditableCell
                          value={Math.round((item.margin || 0) * 10) / 10}
                          type="number"
                          display={
                            <span className={item.margin > 15 ? 'text-emerald-600' : 'text-[#0D1830]'}>
                              {(item.margin || 0).toFixed(0)}%
                            </span>
                          }
                          onSave={onMarginEdit}
                          testid={`item-margin-${item.qi_id}`}
                        />
                      </TableCell>
                      <TableCell>
                        <button onClick={() => removeItem(item.qi_id)} className="p-0.5 rounded hover:bg-red-50">
                          <Trash2 className="w-3 h-3 text-red-400" />
                        </button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </div>
      )}

      <Dialog open={showCreateQuote} onOpenChange={setShowCreateQuote}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-sm">New Quote</DialogTitle>
            <DialogDescription>
              A quote number will be assigned automatically (next: QUO###).
              You can edit the customer, terms, and other details from the quote header after it's created.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateQuote(false)} className="rounded-full text-xs">Cancel</Button>
            <Button onClick={createQuote} className="bg-[#0D1830] text-white rounded-full text-xs hover:opacity-90" data-testid="create-quote-btn">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Quote Header — customer dropdown is sourced from project recipients.
          Hybrid model: recipient_id is a live link, but customer_name/email are
          a snapshot at the moment of saving so historical quotes don't silently
          change if the recipient is later edited. */}
      <Dialog open={showEditHeader} onOpenChange={(o) => { setShowEditHeader(o); if (!o) setHeaderForm(null); }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-sm">Edit Quote Header</DialogTitle>
            <DialogDescription>Update the quote details, customer, and terms.</DialogDescription>
          </DialogHeader>
          {headerForm && (
            <div className="space-y-3 py-2 text-xs">
              <div>
                <label className="block text-[#446E87] font-semibold mb-1">Quote name</label>
                <Input
                  value={headerForm.quote_name}
                  onChange={(e) => setHeaderForm({ ...headerForm, quote_name: e.target.value })}
                  className="text-xs"
                  data-testid="edit-quote-name"
                />
              </div>
              <div>
                <label className="block text-[#446E87] font-semibold mb-1">
                  Customer <span className="font-normal text-[#72A4BF]">(from project contacts)</span>
                </label>
                <select
                  value={headerForm.recipient_id || ''}
                  onChange={(e) => onPickRecipient(e.target.value)}
                  className="w-full text-xs border border-[#E2E8ED] rounded-md px-3 py-2 bg-white"
                  data-testid="edit-quote-recipient"
                >
                  <option value="">— Select a contact or enter manually below —</option>
                  {recipients.map(r => (
                    <option key={r.recipient_id} value={r.recipient_id}>
                      {r.contact_name || r.company_name || '(unnamed)'}{r.company_name && r.contact_name ? ` — ${r.company_name}` : ''}
                    </option>
                  ))}
                </select>
                {recipients.length === 0 && (
                  <p className="text-[10px] text-[#72A4BF] mt-1">
                    No contacts yet. Add them under Project → Quote Recipients to enable the dropdown.
                  </p>
                )}
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-[#446E87] font-semibold mb-1">Customer name</label>
                  <Input
                    value={headerForm.customer_name}
                    onChange={(e) => setHeaderForm({ ...headerForm, customer_name: e.target.value })}
                    className="text-xs"
                    data-testid="edit-quote-customer-name"
                  />
                </div>
                <div>
                  <label className="block text-[#446E87] font-semibold mb-1">Customer email</label>
                  <Input
                    type="email"
                    value={headerForm.customer_email}
                    onChange={(e) => setHeaderForm({ ...headerForm, customer_email: e.target.value })}
                    className="text-xs"
                    data-testid="edit-quote-customer-email"
                  />
                </div>
              </div>
              <div>
                <label className="block text-[#446E87] font-semibold mb-1">Valid until</label>
                <Input
                  type="date"
                  value={headerForm.valid_until ? String(headerForm.valid_until).slice(0, 10) : ''}
                  onChange={(e) => setHeaderForm({ ...headerForm, valid_until: e.target.value })}
                  className="text-xs"
                  data-testid="edit-quote-valid-until"
                />
              </div>
              <div>
                <label className="block text-[#446E87] font-semibold mb-1">Terms & conditions</label>
                <Textarea
                  rows={4}
                  value={headerForm.terms_conditions}
                  onChange={(e) => setHeaderForm({ ...headerForm, terms_conditions: e.target.value })}
                  className="text-xs"
                  data-testid="edit-quote-terms"
                />
              </div>
              <div>
                <label className="block text-[#446E87] font-semibold mb-1">Notes</label>
                <Textarea
                  rows={3}
                  value={headerForm.notes}
                  onChange={(e) => setHeaderForm({ ...headerForm, notes: e.target.value })}
                  className="text-xs"
                  data-testid="edit-quote-notes"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditHeader(false)} className="rounded-full text-xs">Cancel</Button>
            <Button onClick={saveHeader} className="bg-[#0D1830] text-white rounded-full text-xs hover:opacity-90" data-testid="save-quote-header-btn">
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Duplicate Quote — copies all items + header to a new quote (auto-numbered).
          Optionally points it at a different project recipient (re-snapshots
          customer name/email from the picked contact). Status resets to draft. */}
      <Dialog open={showDuplicate} onOpenChange={setShowDuplicate}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-sm">Duplicate Quote</DialogTitle>
            <DialogDescription>
              Creates a copy with all items, terms, and notes. A new quote number is assigned automatically.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2 text-xs">
            <label className="block text-[#446E87] font-semibold">
              Send to a different contact? <span className="font-normal text-[#72A4BF]">(optional)</span>
            </label>
            <select
              value={duplicateRecipientId}
              onChange={(e) => setDuplicateRecipientId(e.target.value)}
              className="w-full text-xs border border-[#E2E8ED] rounded-md px-3 py-2 bg-white"
              data-testid="duplicate-recipient-select"
            >
              <option value="">— Keep current customer —</option>
              {recipients.map(r => (
                <option key={r.recipient_id} value={r.recipient_id}>
                  {r.contact_name || r.company_name || '(unnamed)'}{r.company_name && r.contact_name ? ` — ${r.company_name}` : ''}
                </option>
              ))}
            </select>
            {recipients.length === 0 && (
              <p className="text-[10px] text-[#72A4BF]">
                Add contacts under Project → Quote Recipients to enable per-contact duplication.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDuplicate(false)} className="rounded-full text-xs" disabled={duplicating}>
              Cancel
            </Button>
            <Button onClick={duplicateQuote} className="bg-[#0D1830] text-white rounded-full text-xs hover:opacity-90" disabled={duplicating} data-testid="confirm-duplicate-btn">
              {duplicating ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Copy className="w-3 h-3 mr-1" />}
              Duplicate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send-quote dialog: edit recipient/subject/body before emailing. */}
      <Dialog open={showSendDialog} onOpenChange={(o) => { if (!sendingQuote) setShowSendDialog(o); }}>
        <DialogContent className="sm:max-w-lg" data-testid="send-quote-dialog">
          <DialogHeader>
            <DialogTitle className="text-base text-[#0D1830]">Send quote by email</DialogTitle>
            <DialogDescription className="text-xs text-[#446E87]">
              Edit any field before sending. The customer will also receive a "View Quote" button linked to the online version.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <label className="block text-[10px] font-semibold uppercase tracking-wider text-[#446E87] mb-1">To</label>
              <Input
                type="email"
                value={sendForm.to_email}
                onChange={(e) => setSendForm(prev => ({ ...prev, to_email: e.target.value }))}
                placeholder="customer@example.com"
                className="text-xs"
                data-testid="send-quote-to"
              />
            </div>
            <div>
              <label className="block text-[10px] font-semibold uppercase tracking-wider text-[#446E87] mb-1">Subject</label>
              <Input
                type="text"
                value={sendForm.subject}
                onChange={(e) => setSendForm(prev => ({ ...prev, subject: e.target.value }))}
                placeholder="Quote: ..."
                className="text-xs"
                data-testid="send-quote-subject"
              />
            </div>
            <div>
              <label className="block text-[10px] font-semibold uppercase tracking-wider text-[#446E87] mb-1">Message</label>
              <Textarea
                value={sendForm.message}
                onChange={(e) => setSendForm(prev => ({ ...prev, message: e.target.value }))}
                rows={8}
                className="text-xs font-mono leading-relaxed"
                data-testid="send-quote-message"
              />
              <p className="text-[10px] text-[#72A4BF] mt-1">Line breaks are preserved in the email.</p>
            </div>
            <div>
              <label className="block text-[10px] font-semibold uppercase tracking-wider text-[#446E87] mb-1">Attachment</label>
              <div
                className="inline-flex items-center gap-2 px-2.5 py-1.5 bg-[#F7FAFB] border border-[#E2E8ED] rounded-md"
                data-testid="send-quote-attachment-chip"
              >
                <FileType className="w-3.5 h-3.5 text-red-500" />
                <span className="text-xs font-medium text-[#0D1830]">
                  {((activeQuote?.quote_name || activeQuote?.quote_number || 'quote').replace(/\s+/g, '_'))}.pdf
                </span>
                <span className="text-[10px] text-[#72A4BF] ml-1">auto-attached</span>
              </div>
              <p className="text-[10px] text-[#72A4BF] mt-1">The current v5 PDF is generated and attached when you send.</p>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowSendDialog(false)}
              disabled={sendingQuote}
              className="rounded-full text-xs"
              data-testid="send-quote-cancel"
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={sendQuoteEmail}
              disabled={sendingQuote || !sendForm.to_email.trim() || !sendForm.subject.trim()}
              className="bg-[#0D1830] text-white rounded-full text-xs hover:opacity-90"
              data-testid="send-quote-confirm"
            >
              {sendingQuote ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Send className="w-3 h-3 mr-1" />}
              Send quote
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


/**
 * Quotes tab — list view per UIDESIGN1 page 2.
 *
 * Master/detail UI:
 *   - Master: searchable + filterable table of project quotes.
 *   - Detail: existing QuoteWorksheet (with the stepper, edit/duplicate, items
 *     table), reached by clicking a row.
 *   - URL state: `?quote={quote_id}` deep-links to a specific quote on mount.
 *
 * Status pill labels match the stepper (Pass shipped earlier):
 *   draft → Draft | sent/viewed/revised → Pending | accepted → Won | rejected → Lost
 *   valid_until past today → Expired (overlay; takes precedence on the row pill).
 */
function QuotesListPanel({ projectId, project }) {
  // Local fmtCurrency (the one in QuoteWorksheet/ProjectDetail isn't in this scope).
  // 2 decimals to match the quote-view conventions.
  const fmtCurrency = (v) => `$${(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const [searchParams, setSearchParams] = useSearchParams();
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeFilters, setActiveFilters] = useState(new Set()); // empty == "All"
  const [selectedQuoteId, setSelectedQuoteId] = useState(searchParams.get('quote') || null);

  // Reflect URL changes (e.g., back/forward) into local state.
  useEffect(() => {
    setSelectedQuoteId(searchParams.get('quote') || null);
  }, [searchParams]);

  const fetchQuotes = useCallback(async () => {
    try {
      const res = await fetch(`${API}/quotes?project_id=${projectId}`, { credentials: 'include' });
      if (res.ok) setQuotes(await res.json());
    } catch (e) {}
    finally { setLoading(false); }
  }, [projectId]);

  useEffect(() => { fetchQuotes(); }, [fetchQuotes]);

  // Map UI label → set of backend statuses it includes.
  const FILTER_TO_STATUSES = {
    Draft: ['draft'],
    Pending: ['sent', 'viewed', 'revised'],
    Won: ['accepted'],
    Lost: ['rejected'],
    // Expired is computed from valid_until, not status.
  };

  const isExpired = (q) => {
    if (!q.valid_until) return false;
    try {
      return new Date(q.valid_until) < new Date(new Date().toDateString());
    } catch { return false; }
  };

  // Map a quote to its display label (Pending takes precedence over backend names).
  const labelFor = (q) => {
    if (isExpired(q) && q.status !== 'accepted') return 'Expired';
    if (q.status === 'draft') return 'Draft';
    if (q.status === 'accepted') return 'Won';
    if (q.status === 'rejected') return 'Lost';
    if (['sent', 'viewed', 'revised'].includes(q.status)) return 'Pending';
    return q.status || 'Draft';
  };

  const toggleFilter = (label) => {
    setActiveFilters(prev => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label); else next.add(label);
      return next;
    });
  };

  const filteredQuotes = useMemo(() => {
    const q = (search || '').toLowerCase().trim();
    return quotes.filter(quote => {
      // Text search across number / name / customer
      if (q) {
        const hay = `${quote.quote_number || ''} ${quote.quote_name || ''} ${quote.customer_name || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      // Status filters (multi-select; empty set = All)
      if (activeFilters.size > 0) {
        const label = labelFor(quote);
        if (!activeFilters.has(label)) return false;
      }
      return true;
    });
  }, [quotes, search, activeFilters]);

  const openQuote = (quoteId) => {
    setSelectedQuoteId(quoteId);
    const next = new URLSearchParams(searchParams);
    next.set('quote', quoteId);
    setSearchParams(next, { replace: false });
  };

  const closeQuote = () => {
    setSelectedQuoteId(null);
    const next = new URLSearchParams(searchParams);
    next.delete('quote');
    setSearchParams(next, { replace: false });
  };

  const fmtCreated = (iso) => {
    if (!iso) return '—';
    try {
      const d = new Date(iso);
      return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' });
    } catch { return '—'; }
  };

  // Status pill color palette (matches the Pass-15 stepper terminals).
  const STATUS_PILL_CLASS = {
    Draft:   'bg-slate-100 text-slate-700 border-slate-200',
    Pending: 'bg-amber-50 text-amber-700 border-amber-200',
    Won:     'bg-emerald-50 text-emerald-700 border-emerald-200',
    Lost:    'bg-red-50 text-red-700 border-red-200',
    Expired: 'bg-gray-100 text-gray-600 border-gray-200',
  };

  const FILTER_PILLS = ['All', 'Draft', 'Pending', 'Won', 'Lost', 'Expired'];

  // Detail view: render existing QuoteWorksheet with a Back button.
  if (selectedQuoteId) {
    return (
      <div className="space-y-3" data-testid="quotes-detail-view">
        <button
          type="button"
          onClick={closeQuote}
          className="text-xs font-semibold text-[#446E87] hover:text-[#0D1830] flex items-center gap-1"
          data-testid="back-to-quotes-list"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          Back to quotes
        </button>
        <div className="border border-[#E2E8ED] rounded-xl overflow-hidden bg-white" style={{ height: 'calc(100vh - 280px)', minHeight: 600 }}>
          <QuoteWorksheet projectId={projectId} quoteId={selectedQuoteId} onQuoteIdChange={openQuote} />
        </div>
      </div>
    );
  }

  // Master view
  return (
    <div className="space-y-3" data-testid="quotes-list-view">
      {/* Search + filter row */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
        <div className="flex items-center gap-2 flex-1">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by quote #, name, or customer..."
            className="text-xs max-w-sm"
            data-testid="quotes-list-search"
          />
        </div>
        <div className="flex items-center gap-1.5 flex-wrap" data-testid="quotes-list-filters">
          {FILTER_PILLS.map(label => {
            const isAll = label === 'All';
            const active = isAll ? activeFilters.size === 0 : activeFilters.has(label);
            return (
              <button
                key={label}
                type="button"
                onClick={() => isAll ? setActiveFilters(new Set()) : toggleFilter(label)}
                data-testid={`quotes-filter-${label.toLowerCase()}`}
                className={`px-2.5 py-1 rounded-full border text-[10px] font-semibold transition-colors
                  ${active
                    ? 'bg-[#0D1830] text-white border-[#0D1830]'
                    : 'bg-white text-[#446E87] border-[#E2E8ED] hover:bg-[#F7FAFB]'}`}
              >
                {label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="py-16 flex items-center justify-center"><Loader2 className="w-5 h-5 text-[#1D4053] animate-spin" /></div>
      ) : filteredQuotes.length === 0 ? (
        <Card className="border-dashed border-2 border-[#E2E8ED] shadow-none">
          <CardContent className="py-12 text-center">
            <FileText className="w-8 h-8 text-[#1D4053]/30 mx-auto mb-2" />
            <p className="text-xs text-[#72A4BF] mb-3">
              {quotes.length === 0 ? 'No quotes yet for this project.' : 'No quotes match your filters.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-[#E2E8ED] shadow-none overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="border-[#E2E8ED] bg-[#F7FAFB] hover:bg-[#F7FAFB]">
                <TableHead className="text-[10px] uppercase tracking-wider text-[#72A4BF] font-semibold px-4 py-3 w-24">No.</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider text-[#72A4BF] font-semibold px-4 py-3">Customer Name</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider text-[#72A4BF] font-semibold px-4 py-3 w-28">Created</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider text-[#72A4BF] font-semibold px-4 py-3 w-24">Status</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider text-[#72A4BF] font-semibold px-4 py-3 w-28">Valid Until</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider text-[#72A4BF] font-semibold px-4 py-3 w-28 text-right">Total</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredQuotes.map((q) => {
                const label = labelFor(q);
                const expired = label === 'Expired';
                return (
                  <TableRow
                    key={q.quote_id}
                    onClick={() => openQuote(q.quote_id)}
                    className="border-[#F0F4F7] hover:bg-[#72A4BF]/5 transition-colors cursor-pointer"
                    data-testid={`quotes-row-${q.quote_id}`}
                  >
                    <TableCell className="px-4 py-3">
                      <span className="text-[11px] font-bold tracking-wide text-[#0D1830]">
                        {q.quote_number || '—'}
                      </span>
                    </TableCell>
                    <TableCell className="px-4 py-3 text-xs font-medium text-[#0D1830]">
                      {q.customer_name || q.quote_name || '—'}
                    </TableCell>
                    <TableCell className="px-4 py-3 text-xs text-[#446E87] whitespace-nowrap">
                      {fmtCreated(q.created_at)}
                    </TableCell>
                    <TableCell className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[10px] font-semibold ${STATUS_PILL_CLASS[label] || STATUS_PILL_CLASS.Draft}`}>
                        {label}
                      </span>
                    </TableCell>
                    <TableCell className={`px-4 py-3 text-xs whitespace-nowrap ${expired ? 'text-red-600 font-semibold' : 'text-[#446E87]'}`}>
                      {q.valid_until ? fmtCreated(q.valid_until) : '—'}
                    </TableCell>
                    <TableCell className="px-4 py-3 text-xs font-semibold text-[#0D1830] text-right tabular-nums">
                      {fmtCurrency(q.total_price || 0)}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Card>
      )}

      <p className="text-[10px] text-[#72A4BF] text-center mt-2">
        {filteredQuotes.length} of {quotes.length} quote{quotes.length !== 1 ? 's' : ''}
      </p>
    </div>
  );
}


function CompareView({ projectId }) {
  const [versions, setVersions] = useState([]);
  const [v1, setV1] = useState('');
  const [v2, setV2] = useState('');
  const [diff, setDiff] = useState(null);
  const [loading, setLoading] = useState(false);
  const [versionsLoading, setVersionsLoading] = useState(true);

  useEffect(() => {
    const fetchVersions = async () => {
      try {
        const res = await fetch(`${API}/projects/${projectId}/versions`, { credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          setVersions(data);
          if (data.length >= 2) { setV1(data[1].version.toString()); setV2(data[0].version.toString()); }
        }
      } catch (e) {}
      finally { setVersionsLoading(false); }
    };
    fetchVersions();
  }, [projectId]);

  const runCompare = async () => {
    if (!v1 || !v2 || v1 === v2) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/projects/${projectId}/compare?v1=${v1}&v2=${v2}`, { credentials: 'include' });
      if (res.ok) setDiff(await res.json());
    } catch (e) {}
    finally { setLoading(false); }
  };

  useEffect(() => { if (v1 && v2 && v1 !== v2) runCompare(); }, [v1, v2]);

  const fmtC = (v) => `$${(v || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

  const changeTypeConfig = {
    added: { label: 'New', color: 'bg-blue-50 text-blue-700 border-blue-200', icon: CirclePlus, rowBg: 'bg-blue-50/30' },
    removed: { label: 'Removed', color: 'bg-red-50 text-red-600 border-red-200', icon: X, rowBg: 'bg-red-50/30' },
    increased: { label: 'Increased', color: 'bg-[#72A4BF]/10 text-[#0D1830] border-[#1D4053]/20', icon: ArrowUp, rowBg: 'bg-[#72A4BF]/10' },
    decreased: { label: 'Decreased', color: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: ArrowDown, rowBg: 'bg-emerald-50/30' },
    unchanged: { label: 'Same', color: 'bg-gray-50 text-gray-500 border-gray-200', icon: Minus, rowBg: '' },
  };

  if (versionsLoading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 text-[#1D4053] animate-spin" /></div>;

  if (versions.length < 2) {
    return (
      <Card className="border-dashed border-2 border-[#E2E8ED] shadow-none">
        <CardContent className="py-16 text-center">
          <GitCompare className="w-12 h-12 text-[#1D4053]/30 mx-auto mb-3" />
          <h3 className="text-base font-semibold text-[#0D1830] mb-1">Quick Compare</h3>
          <p className="text-sm text-[#72A4BF] max-w-md mx-auto">
            Run the AI analysis at least twice to compare versions. Each analysis creates a new version, allowing you to compare quantity and cost changes.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div data-testid="compare-view">
      {/* Version selectors */}
      <Card className="border-[#E2E8ED] shadow-none mb-6">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row items-center gap-4">
            <div className="flex-1">
              <label className="text-xs font-medium text-[#446E87] mb-1 block">Version A (Baseline)</label>
              <select value={v1} onChange={(e) => setV1(e.target.value)} className="w-full h-9 rounded-lg border border-[#E2E8ED] px-3 text-sm bg-white" data-testid="version-a-select">
                {versions.map(v => (
                  <option key={v.version} value={v.version}>
                    v{v.version} — {new Date(v.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    {v.is_latest ? ' (latest)' : ''} — {fmtC(v.summary?.total_estimated_cost)}
                  </option>
                ))}
              </select>
            </div>
            <GitCompare className="w-5 h-5 text-[#1D4053] shrink-0 mt-4" />
            <div className="flex-1">
              <label className="text-xs font-medium text-[#446E87] mb-1 block">Version B (Comparison)</label>
              <select value={v2} onChange={(e) => setV2(e.target.value)} className="w-full h-9 rounded-lg border border-[#E2E8ED] px-3 text-sm bg-white" data-testid="version-b-select">
                {versions.map(v => (
                  <option key={v.version} value={v.version}>
                    v{v.version} — {new Date(v.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    {v.is_latest ? ' (latest)' : ''} — {fmtC(v.summary?.total_estimated_cost)}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {loading && <div className="flex items-center justify-center py-10"><Loader2 className="w-6 h-6 text-[#1D4053] animate-spin" /></div>}

      {diff && !loading && (
        <>
          {/* Summary diff */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6" data-testid="diff-summary">
            <Card className={`border shadow-none ${diff.summary_diff.cost_change > 0 ? 'border-red-200 bg-red-50/20' : diff.summary_diff.cost_change < 0 ? 'border-emerald-200 bg-emerald-50/20' : 'border-[#E2E8ED]'}`}>
              <CardContent className="p-3 text-center">
                <p className="text-xs text-[#72A4BF]">Cost Change</p>
                <p className={`text-lg font-bold ${diff.summary_diff.cost_change > 0 ? 'text-red-600' : diff.summary_diff.cost_change < 0 ? 'text-emerald-600' : 'text-[#0D1830]'}`}>
                  {diff.summary_diff.cost_change > 0 ? '+' : ''}{fmtC(diff.summary_diff.cost_change)}
                </p>
              </CardContent>
            </Card>
            <Card className="border-blue-200 shadow-none bg-blue-50/20">
              <CardContent className="p-3 text-center">
                <p className="text-xs text-[#72A4BF]">New Items</p>
                <p className="text-lg font-bold text-blue-700">{diff.summary_diff.added}</p>
              </CardContent>
            </Card>
            <Card className="border-red-200 shadow-none bg-red-50/20">
              <CardContent className="p-3 text-center">
                <p className="text-xs text-[#72A4BF]">Removed</p>
                <p className="text-lg font-bold text-red-600">{diff.summary_diff.removed}</p>
              </CardContent>
            </Card>
            <Card className="border-[#1D4053]/20 shadow-none bg-[#72A4BF]/10">
              <CardContent className="p-3 text-center">
                <p className="text-xs text-[#72A4BF]">Increased</p>
                <p className="text-lg font-bold text-[#0D1830]">{diff.summary_diff.increased}</p>
              </CardContent>
            </Card>
            <Card className="border-emerald-200 shadow-none bg-emerald-50/20">
              <CardContent className="p-3 text-center">
                <p className="text-xs text-[#72A4BF]">Decreased</p>
                <p className="text-lg font-bold text-emerald-600">{diff.summary_diff.decreased}</p>
              </CardContent>
            </Card>
          </div>

          {/* Diff table */}
          <Card className="border-[#E2E8ED] shadow-none">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <GitCompare className="w-4 h-4 text-[#0D1830]" />
                Component-Level Diff (v{v1} vs v{v2})
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="border-[#E2E8ED]">
                    <TableHead className="text-[10px]">Status</TableHead>
                    <TableHead className="text-[10px]">Division</TableHead>
                    <TableHead className="text-[10px]">Component</TableHead>
                    <TableHead className="text-[10px] text-right">v{v1} Qty</TableHead>
                    <TableHead className="text-[10px] text-right">v{v2} Qty</TableHead>
                    <TableHead className="text-[10px] text-right">Qty Change</TableHead>
                    <TableHead className="text-[10px] text-right">v{v1} Cost</TableHead>
                    <TableHead className="text-[10px] text-right">v{v2} Cost</TableHead>
                    <TableHead className="text-[10px] text-right">Cost Change</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {diff.diffs.filter(d => d.change_type !== 'unchanged').map((d, i) => {
                    const cfg = changeTypeConfig[d.change_type] || changeTypeConfig.unchanged;
                    const Icon = cfg.icon;
                    return (
                      <TableRow key={i} className={`border-[#F0F4F7] ${cfg.rowBg}`}>
                        <TableCell>
                          <Badge className={`${cfg.color} border text-[10px] rounded-full px-1.5 py-0`}>
                            <Icon className="w-2.5 h-2.5 mr-0.5" />{cfg.label}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-[10px] text-[#72A4BF]">{d.division_code} {d.division_name}</TableCell>
                        <TableCell className="text-[10px] font-medium max-w-[180px] truncate">{d.component_name}</TableCell>
                        <TableCell className="text-[10px] text-right">{d.v1_quantity || '-'} {d.unit}</TableCell>
                        <TableCell className="text-[10px] text-right">{d.v2_quantity || '-'} {d.unit}</TableCell>
                        <TableCell className="text-[10px] text-right">
                          <span className={d.qty_change > 0 ? 'text-red-600 font-medium' : d.qty_change < 0 ? 'text-emerald-600 font-medium' : 'text-[#72A4BF]'}>
                            {d.qty_change > 0 ? '+' : ''}{d.qty_change || 0}
                          </span>
                        </TableCell>
                        <TableCell className="text-[10px] text-right">{fmtC(d.v1_cost)}</TableCell>
                        <TableCell className="text-[10px] text-right">{fmtC(d.v2_cost)}</TableCell>
                        <TableCell className="text-[10px] text-right">
                          <span className={d.cost_change > 0 ? 'text-red-600 font-semibold' : d.cost_change < 0 ? 'text-emerald-600 font-semibold' : 'text-[#72A4BF]'}>
                            {d.cost_change > 0 ? '+' : ''}{fmtC(d.cost_change)}
                          </span>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              {diff.diffs.filter(d => d.change_type === 'unchanged').length > 0 && (
                <div className="px-4 py-2 text-xs text-[#72A4BF]/60 border-t border-[#E2E8ED]">
                  {diff.diffs.filter(d => d.change_type === 'unchanged').length} unchanged items not shown
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}


export default function ProjectDetail() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [project, setProject] = useState(null);
  const [files, setFiles] = useState([]);
  const [takeoff, setTakeoff] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [specUploading, setSpecUploading] = useState(false);
  const [specFiles, setSpecFiles] = useState([]);
  const [analyzing, setAnalyzing] = useState(false);
  const [showAnalyzeModal, setShowAnalyzeModal] = useState(false);
  const [selectedTier, setSelectedTier] = useState('pro');
  // Analysis scope: 'full' (every division) or 'div579' (Div 05/06/07/09 focused — faster).
  // Div 06 (wood/lumber) runs on both scopes via an additive lumber pass.
  const [scopeMode, setScopeMode] = useState('full');
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'workspace');
  const [quoteId, setQuoteId] = useState('');
  const prevStatusRef = useRef();
  // Floating AI chat widget state — visible on every tab except Workspace
  // (which already has the AI panel embedded in the right column).
  const [chatWidgetOpen, setChatWidgetOpen] = useState(false);
  // Sub-nav state for the merged tabs (Pass #10).
  // 'drawings' tab has Specs / Assemblies / Drawing Map sub-views.
  // 'takeoff' tab has Summary / Detailed sub-views.
  const [drawingsSubTab, setDrawingsSubTab] = useState('specs');
  const [takeoffViewMode, setTakeoffViewMode] = useState('summary');
  // Pass #16 — multi-select state for the detailed takeoff table.
  // Keyed by `${divisionIdx}|${assemblyIdx}|${componentIdx}` so duplicates
  // across divisions/assemblies don't collide.
  const [selectedTakeoffItems, setSelectedTakeoffItems] = useState(() => new Set());
  const [showAddToQuote, setShowAddToQuote] = useState(false);
  const [addToQuoteName, setAddToQuoteName] = useState('');
  const [addToQuoteTargetId, setAddToQuoteTargetId] = useState('');
  const [projectQuotes, setProjectQuotes] = useState([]);
  const [addingToQuote, setAddingToQuote] = useState(false);
  
  // File grouping state
  const [viewMode, setViewMode] = useState('division'); // 'division' or 'file'
  const [sourceFiles, setSourceFiles] = useState([]);
  const [fileStats, setFileStats] = useState({});
  const [selectedFile, setSelectedFile] = useState('all');
  const [fileGroupedData, setFileGroupedData] = useState(null);

  // Drawing location navigation state (for "View on Drawing" from Audit Trail)
  const [drawingNavPage, setDrawingNavPage] = useState(null);
  const [drawingNavFile, setDrawingNavFile] = useState(null);

  const handleViewOnDrawing = useCallback((sourceFile, pageRef) => {
    setDrawingNavFile(sourceFile || null);
    setDrawingNavPage(pageRef || null);
    setActiveTab('drawings');
    setDrawingsSubTab('drawing-map');
  }, []);

  // Pass #16 — multi-select takeoff helpers.
  const toggleTakeoffItem = (rowKey) => {
    setSelectedTakeoffItems(prev => {
      const next = new Set(prev);
      if (next.has(rowKey)) next.delete(rowKey); else next.add(rowKey);
      return next;
    });
  };

  const clearTakeoffSelection = () => setSelectedTakeoffItems(new Set());

  const openAddToQuote = async () => {
    if (selectedTakeoffItems.size === 0) return;
    // Fetch the project's quotes so the modal can offer a target picker.
    try {
      const res = await fetch(`${API}/quotes?project_id=${projectId}`, { credentials: 'include' });
      const list = res.ok ? await res.json() : [];
      setProjectQuotes(list);
      // Default target: the first quote, OR empty (will prompt to create one).
      setAddToQuoteTargetId(list[0]?.quote_id || '');
      setAddToQuoteName(list.length === 0 ? `Quote for ${project?.name || 'Project'}` : '');
      setShowAddToQuote(true);
    } catch (e) {
      toast.error('Failed to load quotes');
    }
  };

  const performAddToQuote = async () => {
    if (selectedTakeoffItems.size === 0) return;
    setAddingToQuote(true);
    try {
      // Build the items payload from selected component refs.
      // Iterate divisions/assemblies/components and emit the ones whose
      // composite key is in selectedTakeoffItems.
      const itemsPayload = [];
      (divisions || []).forEach((d, di) => {
        (d.assemblies || []).forEach((a, ai) => {
          (a.components || []).forEach((c, ci) => {
            const key = `${di}|${ai}|${ci}`;
            if (!selectedTakeoffItems.has(key)) return;
            itemsPayload.push({
              component_name: c.component_name || '',
              description: c.description || '',
              quantity: c.quantity || 0,
              unit: c.unit || 'EA',
              cost: c.unit_cost || 0,
              price: c.unit_price || (c.unit_cost ? c.unit_cost * 1.2 : 0),
              confidence: c.confidence || 0,
              page_reference: c.page_reference || c.source_file || '',
              division: d.division_name || '',
              assembly: a.assembly_name || a.assembly_code || '',
            });
          });
        });
      });

      if (itemsPayload.length === 0) {
        toast.error('No items to add');
        return;
      }

      // Resolve target quote: existing or newly-created.
      let targetQuoteId = addToQuoteTargetId;
      if (!targetQuoteId) {
        const name = (addToQuoteName || '').trim() || `Quote for ${project?.name || 'Project'}`;
        const cr = await fetch(`${API}/quotes`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ project_id: projectId, quote_name: name }),
        });
        if (!cr.ok) {
          toast.error('Failed to create quote');
          return;
        }
        const created = await cr.json();
        targetQuoteId = created.quote_id;
      }

      const res = await fetch(`${API}/quotes/${targetQuoteId}/items/bulk`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ items: itemsPayload }),
      });
      if (res.ok) {
        const data = await res.json();
        const addedCount = data.added ?? itemsPayload.length;
        const targetLabel = data.quote?.quote_number || data.quote?.quote_name || 'quote';
        toast.success(`Added ${addedCount} item${addedCount !== 1 ? 's' : ''} to ${targetLabel}`);
        clearTakeoffSelection();
        setShowAddToQuote(false);
        // Refresh QuoteWorksheet if it's mounted
        if (window._refreshQuote) window._refreshQuote();
      } else {
        toast.error('Failed to add items to quote');
      }
    } catch (e) {
      toast.error('Failed to add items to quote');
    } finally {
      setAddingToQuote(false);
    }
  };

  const fetchProject = useCallback(async () => {
    try {
      const res = await fetch(`${API}/projects/${projectId}`, { credentials: 'include' });
      if (!res.ok) {
        if (res.status === 404) { navigate('/projects'); return; }
        setLoadError(true);
        return;
      }
      const data = await res.json();
      setProject(data);
      setFiles((data.files || []).filter(f => f.file_type !== 'spec_sheet'));
      setSpecFiles((data.files || []).filter(f => f.file_type === 'spec_sheet'));
      setTakeoff(data.takeoff);
      setAnalyzing(data.status === 'analyzing');
      setLoadError(false);
    } catch {
      setLoadError(true);
    }
    finally { setLoading(false); }
  }, [projectId, navigate]);

  useEffect(() => { fetchProject(); }, [fetchProject]);

  // Auto-switch to Takeoff Data tab on the precise edge analyzing → not-analyzing.
  // Doesn't fire on initial mount (prev is undefined) so users who reload while
  // analyzing don't get yanked off the Documents/Workspace tab.
  useEffect(() => {
    const prev = prevStatusRef.current;
    const cur = project?.status;
    if (prev === 'analyzing' && cur && cur !== 'analyzing') {
      setActiveTab('takeoff');
    }
    prevStatusRef.current = cur;
  }, [project?.status]);

  // Auto-close the floating chat widget when the user switches to the Workspace
  // tab — the AI panel is already embedded there, no need for the floater too.
  useEffect(() => {
    if (activeTab === 'workspace' && chatWidgetOpen) {
      setChatWidgetOpen(false);
    }
  }, [activeTab, chatWidgetOpen]);

  // Esc closes the chat widget.
  useEffect(() => {
    if (!chatWidgetOpen) return;
    const onKey = (e) => { if (e.key === 'Escape') setChatWidgetOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [chatWidgetOpen]);

  // Friendly subtitle for the chat widget header — gives the user (and AI, if
  // we wire it later) a sense of the page context.
  const chatContextLabel = (() => {
    switch (activeTab) {
      case 'upload':   return 'Documents';
      case 'drawings': return `Drawings → ${
        drawingsSubTab === 'specs' ? 'Specs' :
        drawingsSubTab === 'assemblies' ? 'Assemblies' :
        'Drawing Map'
      }`;
      case 'takeoff':  return `Takeoff (${takeoffViewMode === 'detailed' ? 'Detailed' : 'Summary'})`;
      case 'quotes':   return 'Quotes';
      default:         return '';
    }
  })();

  useEffect(() => {
    if (!analyzing) return;
    const interval = setInterval(fetchProject, 5000);
    return () => clearInterval(interval);
  }, [analyzing, fetchProject]);

  // Fetch source files when switching to file view
  const fetchSourceFiles = useCallback(async () => {
    try {
      const res = await fetch(`${API}/projects/${projectId}/takeoff/source-files`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setSourceFiles(data.source_files || []);
        setFileStats(data.file_stats || {});
      }
    } catch (e) {}
  }, [projectId]);

  const fetchFileGroupedData = useCallback(async (fileFilter = '') => {
    try {
      const url = fileFilter && fileFilter !== 'all' 
        ? `${API}/projects/${projectId}/takeoff/by-file?file_filter=${encodeURIComponent(fileFilter)}`
        : `${API}/projects/${projectId}/takeoff/by-file`;
      const res = await fetch(url, { credentials: 'include' });
      if (res.ok) {
        setFileGroupedData(await res.json());
      }
    } catch (e) {}
  }, [projectId]);

  useEffect(() => {
    if (viewMode === 'file' && activeTab === 'takeoff') {
      fetchSourceFiles();
      fetchFileGroupedData(selectedFile);
    }
  }, [viewMode, activeTab, selectedFile, fetchSourceFiles, fetchFileGroupedData]);

  const onDrop = useCallback(async (acceptedFiles) => {
    const pdfs = acceptedFiles.filter(f => f.name.toLowerCase().endsWith('.pdf'));
    if (!pdfs.length) return;
    setUploading(true);
    const fd = new FormData();
    pdfs.forEach(f => fd.append('files', f));
    try {
      const res = await fetch(`${API}/projects/${projectId}/upload`, { method: 'POST', credentials: 'include', body: fd });
      if (res.ok) {
        const data = await res.json();
        if (data.duplicates?.length > 0) {
          toast.warning(`Duplicate file${data.duplicates.length > 1 ? 's' : ''} detected: ${data.duplicates.join(', ')}`, {
            description: 'These files already exist in this project. Duplicates were still uploaded.',
            duration: 6000,
          });
        }
      }
      await fetchProject();
    } catch (e) {}
    finally { setUploading(false); }
  }, [projectId, fetchProject]);

  const onSpecDrop = useCallback(async (acceptedFiles) => {
    const pdfs = acceptedFiles.filter(f => f.name.toLowerCase().endsWith('.pdf'));
    if (!pdfs.length) return;
    setSpecUploading(true);
    const fd = new FormData();
    pdfs.forEach(f => fd.append('files', f));
    try {
      const res = await fetch(`${API}/projects/${projectId}/upload-spec`, { method: 'POST', credentials: 'include', body: fd });
      if (res.ok) {
        const data = await res.json();
        toast.success(`Spec sheet uploaded (${data.total_pages} pages). Processing in background...`);
      }
      await fetchProject();
    } catch (e) {
      toast.error('Failed to upload spec sheet');
    }
    finally { setSpecUploading(false); }
  }, [projectId, fetchProject]);

  const deleteSpecSheet = async () => {
    try {
      await fetch(`${API}/projects/${projectId}/spec-sheet`, { method: 'DELETE', credentials: 'include' });
      toast.info('Spec sheet removed');
      await fetchProject();
    } catch (e) {}
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop, accept: { 'application/pdf': ['.pdf'] }, multiple: true });

  // Maps the user-facing tier to the backend's selected_model bundle.
  // Tier names are intentionally brand-only — never expose LLM names in the UI.
  const TIER_TO_MODEL = {
    nivela: 'hybrid-mini-claude',
    pro:    'hybrid-sonnet-gemini',
    expert: 'hybrid-mini-gemini',
  };

  const startAnalysis = async (tier = 'pro', scope = scopeMode) => {
    setShowAnalyzeModal(false);
    setAnalyzing(true);
    try {
      const res = await fetch(`${API}/projects/${projectId}/analyze`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          selected_model: TIER_TO_MODEL[tier] || TIER_TO_MODEL.pro,
          analysis_mode: scope === 'div579' ? 'div579' : 'full',
          // Division 06 (wood / rough carpentry) runs on both scopes — an
          // additive lumber pass that never alters Div 05/07/09 output.
          include_div06: true,
        }),
      });
      if (!res.ok) {
        setAnalyzing(false);
        if (res.status === 402) {
          let msg = 'Out of Nivela Tokens — recharge to run more analyses.';
          try { const d = await res.json(); if (d?.detail) msg = d.detail; } catch {}
          toast.error(msg);
        }
      }
    } catch { setAnalyzing(false); }
  };

  const cancelAnalysis = async () => {
    try {
      const res = await fetch(`${API}/projects/${projectId}/cancel-analysis`, { method: 'POST', credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setAnalyzing(false);
        toast.info('Analysis cancelled');
        await fetchProject();
      }
    } catch {
      toast.error('Failed to cancel analysis');
    }
  };

  const exportExcel = async () => {
    const res = await fetch(`${API}/projects/${projectId}/export-excel`, { credentials: 'include' });
    if (res.ok) { const blob = await res.blob(); const url = URL.createObjectURL(blob); const a = document.createElement('a'); a.href = url; a.download = `${project?.name || 'takeoff'}.xlsx`; document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url); }
  };

  const exportPdfReport = async () => {
    const res = await fetch(`${API}/projects/${projectId}/export-pdf`, { credentials: 'include' });
    if (res.ok) { const blob = await res.blob(); const url = URL.createObjectURL(blob); const a = document.createElement('a'); a.href = url; a.download = `${project?.name || 'report'}_Report.pdf`; document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url); }
  };

  const fmtCurrency = (v) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(v || 0);

  const handleQuoteUpdate = () => { if (window._refreshQuote) window._refreshQuote(); };

  if (loading) return <div className="min-h-full bg-[#F7FAFB]"><div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 text-[#1D4053] animate-spin" /></div></div>;

  if (loadError) return (
    <div className="min-h-full bg-[#F7FAFB]">
      <PageError onGoBack={() => navigate('/projects')} onRetry={fetchProject} />
    </div>
  );

  const statusConfig = {
    pending: { label: 'Pending', color: 'bg-[#F0F4F7] text-[#446E87]', icon: Clock },
    active: { label: 'Active', color: 'bg-blue-100 text-blue-700', icon: FileText },
    analyzing: { label: 'Analyzing...', color: 'bg-[#72A4BF]/10 text-[#0D1830]', icon: Loader2 },
    completed: { label: 'Completed', color: 'bg-[#E8F5E9] text-[#2E7D32]', icon: CheckCircle2 },
    expired: { label: 'Expired', color: 'bg-orange-100 text-orange-700', icon: Clock },
    error: { label: 'Error', color: 'bg-[#FFEBEE] text-[#C62828]', icon: AlertCircle },
  };
  const sc = statusConfig[project?.status] || statusConfig.pending;
  const SI = sc.icon;
  const divisions = takeoff?.divisions || [];
  const summary = takeoff?.summary || {};

  const formatBidDate = (d) => {
    if (!d) return null;
    try { return new Date(d + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); }
    catch { return d; }
  };

  return (
    <div className="min-h-full bg-[#F7FAFB]" data-testid="project-detail">
      <main className="max-w-[1440px] mx-auto px-6 lg:px-10 py-4">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 mb-4">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/projects')} className="p-1.5 rounded-lg hover:bg-[#72A4BF]/8" data-testid="back-btn"><ArrowLeft className="w-4 h-4 text-[#446E87]" /></button>
            <div>
              <h1 className="text-xl font-bold text-[#0D1830]" data-testid="project-name">{project?.name}</h1>
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                {(() => {
                  const drawingCount = files.filter(f => !f.is_spec_sheet).length;
                  const specCount = files.filter(f => f.is_spec_sheet).length;
                  const pageCount = project?.total_pages || files.reduce((s, f) => s + (f.pages || 0), 0);
                  const parts = [];
                  parts.push(`${drawingCount} drawing${drawingCount !== 1 ? 's' : ''}`);
                  if (specCount > 0) parts.push(`${specCount} spec${specCount !== 1 ? 's' : ''}`);
                  if (pageCount > 0) parts.push(`${pageCount} page${pageCount !== 1 ? 's' : ''}`);
                  return <span className="text-xs text-[#72A4BF]" data-testid="header-doc-summary">{parts.join(' · ')}</span>;
                })()}
                {project?.client_name && <span className="text-xs text-[#446E87]">• {project.client_name}</span>}
                {(project?.bid_start_date || project?.bid_end_date) && (
                  <span className="text-xs text-[#446E87] flex items-center gap-1" data-testid="header-bid-dates">
                    • <Calendar className="w-3 h-3" />
                    {project.bid_start_date && formatBidDate(project.bid_start_date)}
                    {project.bid_start_date && project.bid_end_date && ' — '}
                    {project.bid_end_date && formatBidDate(project.bid_end_date)}
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {takeoff && <Button variant="outline" size="sm" onClick={exportExcel} className="rounded-full text-xs" data-testid="export-btn"><Download className="w-3.5 h-3.5 mr-1" />Excel</Button>}
            {project?.status === 'analyzing' && (
              <Button size="sm" variant="outline" onClick={cancelAnalysis} className="rounded-full text-xs font-semibold border-red-300 text-red-600 hover:bg-red-50" data-testid="cancel-analysis-btn">
                <Ban className="w-3.5 h-3.5 mr-1" />Cancel Analysis
              </Button>
            )}
            {files.length > 0 && project?.status !== 'analyzing' && (
              <Button size="sm" onClick={() => { setSelectedTier('pro'); setShowAnalyzeModal(true); }} disabled={analyzing} className="bg-[#0D1830] text-white rounded-full text-xs font-semibold hover:opacity-90" data-testid="analyze-btn">
                {analyzing ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Play className="w-3.5 h-3.5 mr-1" />}
                {project?.status === 'completed' ? 'Re-Analyze' : 'Run Analysis'}
              </Button>
            )}
          </div>
        </div>

        {/* Run Analysis: tier picker (Nivela / Nivela Pro / Nivela Expert) */}
        <Dialog open={showAnalyzeModal} onOpenChange={setShowAnalyzeModal}>
          <DialogContent className="sm:max-w-3xl">
            <DialogHeader>
              <DialogTitle className="text-lg">Run Analysis</DialogTitle>
              <DialogDescription>Pick the analysis scope and tier for this project.</DialogDescription>
            </DialogHeader>

            {/* Scope toggle: full takeoff vs Div 05/06/07/09 fast track (Div 06 lumber runs on both) */}
            <div className="mb-2" data-testid="analyze-scope-toggle">
              <div className="text-[11px] uppercase tracking-wider text-[#72A4BF] font-semibold mb-1.5">Scope</div>
              <div className="inline-flex gap-1 p-1 bg-[#F7FAFB] border border-[#E2E8ED] rounded-lg">
                {[
                  { id: 'full',   label: 'All divisions',              hint: 'Complete takeoff across every division (includes Div 06 wood)' },
                  { id: 'div579', label: 'Div 05 / 06 / 07 / 09',      hint: 'Metals, Wood, Insulation, Finishes — faster, lower cost' },
                ].map(opt => {
                  const active = scopeMode === opt.id;
                  return (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() => setScopeMode(opt.id)}
                      className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors
                        ${active ? 'bg-[#0D1830] text-white' : 'text-[#446E87] hover:bg-white'}`}
                      data-testid={`analyze-scope-${opt.id}`}
                      title={opt.hint}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
              <div className="text-[10px] text-[#72A4BF] mt-1">
                {scopeMode === 'div579'
                  ? 'Focused mode: Div 05 (Metals), 06 (Wood / Rough Carpentry), 07 (Insulation), 09 (Finishes). Faster runs, lower token cost.'
                  : 'Full mode: every division covered, including Div 06 (Wood). Standard takeoff.'}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 py-2" data-testid="analyze-tier-cards">
              {[
                {
                  id: 'nivela',
                  name: 'Nivela',
                  speed: 'Fast',
                  accuracy: 'Good',
                  precision: 'Standard',
                  ideal: 'Quick estimates, residential / light commercial, pre-bid pricing',
                  tokens: 'Low',
                },
                {
                  id: 'pro',
                  name: 'Nivela Pro',
                  speed: 'Slow',
                  accuracy: 'Excellent',
                  precision: 'High',
                  ideal: 'Complex commercial bids, large/dense drawings, fire-rated assemblies',
                  tokens: 'High',
                  recommended: true,
                },
                {
                  id: 'expert',
                  name: 'Nivela Expert',
                  speed: 'Medium',
                  accuracy: 'Very Good',
                  precision: 'High',
                  ideal: 'Balanced cost vs. detail, mid-size commercial, day-to-day takeoffs',
                  tokens: 'Medium',
                },
              ].map((tier) => {
                const active = selectedTier === tier.id;
                return (
                  <button
                    key={tier.id}
                    type="button"
                    onClick={() => setSelectedTier(tier.id)}
                    data-testid={`analyze-tier-${tier.id}`}
                    className={`text-left rounded-xl border p-4 transition-all
                      ${active ? 'border-[#0D1830] bg-[#0D1830]/5 ring-2 ring-[#0D1830]/20' : 'border-[#E2E8ED] hover:border-[#0D1830]/30'}
                    `}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-sm font-bold text-[#0D1830]">{tier.name}</div>
                      {tier.recommended && (
                        <span className="text-[9px] font-bold uppercase tracking-wider bg-[#0D1830] text-white px-1.5 py-0.5 rounded">Recommended</span>
                      )}
                    </div>
                    <dl className="space-y-1 text-xs">
                      <div className="flex justify-between"><dt className="text-[#72A4BF]">Speed</dt><dd className="font-medium text-[#0D1830]">{tier.speed}</dd></div>
                      <div className="flex justify-between"><dt className="text-[#72A4BF]">Accuracy</dt><dd className="font-medium text-[#0D1830]">{tier.accuracy}</dd></div>
                      <div className="flex justify-between"><dt className="text-[#72A4BF]">Precision</dt><dd className="font-medium text-[#0D1830]">{tier.precision}</dd></div>
                      <div className="flex justify-between"><dt className="text-[#72A4BF]">Token use</dt><dd className="font-medium text-[#0D1830]">{tier.tokens}</dd></div>
                    </dl>
                    <div className="mt-2 pt-2 border-t border-[#E2E8ED]/60">
                      <div className="text-[10px] uppercase tracking-wider text-[#72A4BF] mb-0.5">Ideal for</div>
                      <div className="text-[11px] text-[#446E87] leading-snug">{tier.ideal}</div>
                    </div>
                  </button>
                );
              })}
            </div>
            <DialogFooter>
              <Button variant="outline" size="sm" onClick={() => setShowAnalyzeModal(false)} data-testid="analyze-cancel-btn">Cancel</Button>
              <Button size="sm" onClick={() => startAnalysis(selectedTier, scopeMode)} disabled={analyzing} className="bg-[#0D1830] text-white" data-testid="analyze-confirm-btn">
                <Play className="w-3.5 h-3.5 mr-1" />
                {project?.status === 'completed' ? 'Re-Analyze' : 'Run Analysis'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Add Selected Takeoff Items to Quote (Pass #16) */}
        <Dialog open={showAddToQuote} onOpenChange={setShowAddToQuote}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="text-sm">Add to Quote</DialogTitle>
              <DialogDescription>
                Add {selectedTakeoffItems.size} selected takeoff item{selectedTakeoffItems.size !== 1 ? 's' : ''} to a quote.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 py-2 text-xs">
              {projectQuotes.length === 0 ? (
                <div>
                  <label className="block text-[#446E87] font-semibold mb-1">
                    No quotes exist for this project — create one
                  </label>
                  <Input
                    value={addToQuoteName}
                    onChange={(e) => setAddToQuoteName(e.target.value)}
                    placeholder="Quote name..."
                    className="text-xs"
                    data-testid="add-to-quote-name"
                  />
                </div>
              ) : (
                <div>
                  <label className="block text-[#446E87] font-semibold mb-1">Target quote</label>
                  <select
                    value={addToQuoteTargetId}
                    onChange={(e) => setAddToQuoteTargetId(e.target.value)}
                    className="w-full text-xs border border-[#E2E8ED] rounded-md px-3 py-2 bg-white"
                    data-testid="add-to-quote-target"
                  >
                    {projectQuotes.map(q => (
                      <option key={q.quote_id} value={q.quote_id}>
                        {q.quote_number ? `${q.quote_number} — ` : ''}{q.quote_name || q.customer_name || 'Quote'}
                      </option>
                    ))}
                    <option value="">— Create new quote —</option>
                  </select>
                  {!addToQuoteTargetId && (
                    <Input
                      value={addToQuoteName}
                      onChange={(e) => setAddToQuoteName(e.target.value)}
                      placeholder="New quote name..."
                      className="text-xs mt-2"
                    />
                  )}
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAddToQuote(false)} className="rounded-full text-xs" disabled={addingToQuote}>
                Cancel
              </Button>
              <Button
                onClick={performAddToQuote}
                disabled={addingToQuote}
                className="bg-[#0D1830] text-white rounded-full text-xs hover:opacity-90"
                data-testid="confirm-add-to-quote"
              >
                {addingToQuote ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Plus className="w-3 h-3 mr-1" />}
                Add to Quote
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Project Info Panel - Phase 5 */}
        <div className="mb-4">
          <ProjectInfoPanel project={project} onUpdate={fetchProject} />
        </div>

        {/* Analysis Error Banner */}
        {project?.status === 'error' && (
          <div className="mb-4">
            <AnalysisError
              errorMessage={project?.error_message}
              projectName={project?.name}
              onRetry={startAnalysis}
            />
          </div>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="bg-white border border-[#E2E8ED] rounded-xl p-1 mb-4" data-testid="project-tabs">
            <TabsTrigger value="workspace" className="rounded-lg text-xs data-[state=active]:bg-[#0D1830] data-[state=active]:text-white">Workspace</TabsTrigger>
            <TabsTrigger value="upload" className="rounded-lg text-xs data-[state=active]:bg-[#0D1830] data-[state=active]:text-white">Documents</TabsTrigger>
            <TabsTrigger value="drawings" className="rounded-lg text-xs data-[state=active]:bg-[#0D1830] data-[state=active]:text-white" data-testid="drawings-tab-trigger">
              <MapPin className="w-3 h-3 mr-1" />Drawings
              {project?.spec_sections_count > 0 && <Badge className="ml-1 text-[9px] px-1.5 py-0 rounded-full bg-indigo-100 text-indigo-600 border-0">{project.spec_sections_count}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="takeoff" className="rounded-lg text-xs data-[state=active]:bg-[#0D1830] data-[state=active]:text-white">Takeoff Data</TabsTrigger>
            <TabsTrigger value="quotes" className="rounded-lg text-xs data-[state=active]:bg-[#0D1830] data-[state=active]:text-white">
              <FileText className="w-3 h-3 mr-1" />Quotes
            </TabsTrigger>
          </TabsList>

          {/* WORKSPACE - The Core */}
          <TabsContent value="workspace" className="mt-0" data-testid="workspace-tab">
            {project?.status === 'analyzing' && (() => {
              const prog = project?.analysis_progress;
              const status = prog?.status || 'extracting';
              const startedAt = prog?.started_at ? new Date(prog.started_at) : null;
              const completedChunks = prog?.completed_chunks || 0;
              const totalChunks = prog?.total_chunks || 1;
              const concurrency = prog?.concurrency || 5;

              let pct = 5;
              if (status === 'extracting') pct = 8;
              else if (status === 'analyzing') pct = 10 + Math.min(75, Math.round((completedChunks / totalChunks) * 75));
              else if (status === 'merging') pct = 92;

              let etaText = 'Preparing analysis...';
              if (startedAt && completedChunks > 0 && status === 'analyzing') {
                const elapsed = (Date.now() - startedAt.getTime()) / 1000;
                const perChunk = elapsed / completedChunks;
                const rem = Math.max(0, Math.round((perChunk * (totalChunks - completedChunks)) / Math.max(concurrency / 2, 1)));
                if (rem > 60) etaText = `~${Math.ceil(rem / 60)} min remaining`;
                else etaText = `~${rem}s remaining`;
              } else if (status === 'extracting') {
                etaText = 'Extracting pages & schedules...';
              } else if (status === 'merging') {
                etaText = 'Merging results...';
              } else if (status === 'analyzing' && completedChunks === 0) {
                const totalPages = prog?.total_pages || 0;
                const estSec = Math.ceil((totalPages / 3) / 5) * 20;
                if (estSec > 60) etaText = `~${Math.ceil(estSec / 60)} min estimated`;
                else etaText = `~${estSec}s estimated`;
              }

              return (
                <Card className="mb-4 border-[#1D4053]/20 shadow-none bg-[#72A4BF]/10" data-testid="analysis-progress">
                  <CardContent className="py-5">
                    <div className="flex items-center justify-center gap-2 mb-3">
                      <Loader2 className="w-5 h-5 text-[#0D1830] animate-spin" />
                      <p className="text-sm font-medium text-[#0D1830]">
                        {status === 'extracting' ? 'Preparing...' : status === 'merging' ? 'Merging Results...' : `Analyzing ${completedChunks}/${totalChunks} chunks`}
                      </p>
                    </div>
                    <Progress value={pct} className="max-w-sm mx-auto h-1.5 bg-[#1D4053]/10 [&>div]:bg-[#1D4053]" />
                    <p className="text-[10px] text-[#72A4BF] mt-1.5 text-center" data-testid="analysis-eta">{etaText}</p>
                    {prog?.total_pages > 0 && (
                      <p className="text-[10px] text-[#72A4BF]/70 mt-1 text-center">
                        {prog.total_pages} pages {totalChunks > 0 ? `in ${totalChunks} chunks` : ''}
                      </p>
                    )}
                  </CardContent>
                </Card>
              );
            })()}
            {/* Workspace: chat + quote only. Drawing browsing lives in the
                Drawings tab → Drawing Map sub-view. */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-4" style={{ height: 'calc(100vh - 220px)' }}>
              {/* AI Assistant - Left */}
              <div className="lg:col-span-5 border border-[#E2E8ED] rounded-xl bg-white overflow-hidden">
                <AIAssistant projectId={projectId} quoteId={quoteId} onQuoteUpdate={handleQuoteUpdate} />
              </div>
              {/* Quote Worksheet - Right */}
              <div className="lg:col-span-7 border border-[#E2E8ED] rounded-xl bg-white overflow-hidden">
                <QuoteWorksheet projectId={projectId} quoteId={quoteId} onQuoteIdChange={setQuoteId} />
              </div>
            </div>
          </TabsContent>

          {/* DOCUMENTS */}
          <TabsContent value="upload" data-testid="upload-tab">
            <Card className="border-[#E2E8ED] shadow-none">
              <CardContent className="p-6">
                <div {...getRootProps()} className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${isDragActive ? 'border-[#1D4053] bg-[#72A4BF]/10' : 'border-[#E2E8ED] hover:border-[#1D4053]/40'}`} data-testid="dropzone">
                  <input {...getInputProps()} />
                  {uploading ? <Loader2 className="w-6 h-6 text-[#1D4053] animate-spin mx-auto" /> : (
                    <><Upload className="w-8 h-8 text-[#0D1830] mx-auto mb-3" /><p className="text-sm font-medium text-[#0D1830]">Drag & drop PDF files</p><p className="text-xs text-[#72A4BF] mt-1">Drawings, specs, and schedules</p></>
                  )}
                </div>
                {files.length > 0 && (
                  <div className="mt-4 space-y-2" data-testid="files-list">
                    {files.map(f => (
                      <div key={f.file_id} className="flex items-center gap-3 p-3 rounded-lg bg-[#F7FAFB] border border-[#E2E8ED]">
                        <FileText className="w-4 h-4 text-[#0D1830]" />
                        <span className="text-sm text-[#0D1830] truncate flex-1">{f.filename}</span>
                        <span className="text-xs text-[#72A4BF]/60">{(f.file_size / 1024).toFixed(0)} KB</span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Spec Sheet Section */}
            <Card className="border-[#E2E8ED] shadow-none mt-4" data-testid="spec-sheet-section">
              <CardContent className="pt-5">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h3 className="text-sm font-semibold text-[#0D1830] flex items-center gap-2">
                      <BookOpen className="w-4 h-4" />
                      Specification Sheet
                      <span className="text-[10px] font-normal text-[#72A4BF]">(optional)</span>
                    </h3>
                    <p className="text-[10px] text-[#72A4BF] mt-0.5">Upload the project spec book for product details, calculation rules, and installation requirements</p>
                  </div>
                  {specFiles.length > 0 && (
                    <Button variant="outline" size="sm" onClick={deleteSpecSheet} className="text-xs h-7 rounded-full text-red-500 border-red-200 hover:bg-red-50" data-testid="delete-spec-btn">
                      <Trash2 className="w-3 h-3 mr-1" />Remove
                    </Button>
                  )}
                </div>

                {specFiles.length === 0 ? (
                  <div
                    className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-all border-[#E2E8ED] hover:border-[#1D4053]/40`}
                    onClick={() => document.getElementById('spec-upload-input')?.click()}
                    data-testid="spec-dropzone"
                  >
                    <input
                      id="spec-upload-input" type="file" accept=".pdf" className="hidden"
                      onChange={(e) => { if (e.target.files?.length) onSpecDrop(Array.from(e.target.files)); e.target.value = ''; }}
                    />
                    {specUploading ? <Loader2 className="w-5 h-5 text-[#1D4053] animate-spin mx-auto" /> : (
                      <>
                        <BookOpen className="w-6 h-6 text-[#72A4BF] mx-auto mb-2" />
                        <p className="text-xs font-medium text-[#0D1830]">Upload Spec Sheet (PDF)</p>
                        <p className="text-[10px] text-[#72A4BF] mt-1">200-1000+ pages — processed with Gemini Flash OCR</p>
                      </>
                    )}
                  </div>
                ) : (
                  <div className="space-y-2">
                    {specFiles.map(f => (
                      <div key={f.file_id} className="flex items-center gap-3 p-3 rounded-lg bg-indigo-50/50 border border-indigo-200">
                        <BookOpen className="w-4 h-4 text-indigo-600" />
                        <span className="text-sm text-[#0D1830] truncate flex-1">{f.filename}</span>
                        <span className="text-xs text-[#72A4BF]">{f.pages || '?'} pages</span>
                      </div>
                    ))}
                    {project?.spec_processing_status === 'processing' && (
                      <div className="flex items-center gap-2 text-xs text-indigo-600 px-1">
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        Processing spec sheet...
                      </div>
                    )}
                    {project?.spec_processing_status === 'completed' && (
                      <div className="flex items-center gap-2 text-xs text-emerald-600 px-1" data-testid="spec-status-complete">
                        <CheckCircle2 className="w-3.5 h-3.5" />
                        {project.spec_sections_count || 0} spec sections extracted — will be used in next takeoff
                      </div>
                    )}
                    {project?.spec_processing_status === 'error' && (
                      <div className="flex items-center gap-2 text-xs text-red-500 px-1">
                        <AlertCircle className="w-3.5 h-3.5" />
                        Spec processing failed. Try re-uploading.
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>


          {/* SPECS TAB */}
          {/* DRAWINGS TAB — merged Specs / Assemblies / Drawing Map (Pass #10) */}
          <TabsContent value="drawings" data-testid="drawings-tab">
            <div className="flex items-center gap-1.5 mb-3 bg-white border border-[#E2E8ED] rounded-lg p-1 w-fit" data-testid="drawings-sub-nav">
              {[
                { id: 'specs',        label: 'Specs',       testid: 'specs-sub-tab' },
                { id: 'assemblies',   label: 'Assemblies',  testid: 'assemblies-sub-tab' },
              ].map(sub => (
                <button
                  key={sub.id}
                  type="button"
                  onClick={() => setDrawingsSubTab(sub.id)}
                  data-testid={sub.testid}
                  className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors
                    ${drawingsSubTab === sub.id ? 'bg-[#0D1830] text-white' : 'text-[#446E87] hover:bg-[#F7FAFB]'}`}
                >
                  {sub.label}
                </button>
              ))}
            </div>
            {drawingsSubTab === 'specs' && (
              <div data-testid="specs-tab-content"><SpecViewer projectId={projectId} /></div>
            )}
            {drawingsSubTab === 'assemblies' && (
              <div data-testid="assemblies-tab-content">
                <AssemblyExtractor projectId={projectId} takeoff={takeoff} projectStatus={project?.status} onExtracted={fetchProject} />
              </div>
            )}
            {drawingsSubTab === 'drawing-map' && (
              <div data-testid="drawing-map-tab-content">
                <DrawingLocationPanel
                  projectId={projectId}
                  files={files}
                  initialPage={drawingNavPage}
                  initialFile={drawingNavFile}
                />
              </div>
            )}
          </TabsContent>

          {/* TAKEOFF DATA — merged Summary + Detailed (Pass #10) */}
          <TabsContent value="takeoff" data-testid="takeoff-tab">
            {(divisions.length > 0 || (summary.total_divisions > 0)) && (
              <div className="flex items-center gap-1.5 mb-3 bg-white border border-[#E2E8ED] rounded-lg p-1 w-fit" data-testid="takeoff-view-toggle">
                {[
                  { id: 'summary',  label: 'Summary' },
                  { id: 'detailed', label: 'Detailed' },
                ].map(view => (
                  <button
                    key={view.id}
                    type="button"
                    onClick={() => setTakeoffViewMode(view.id)}
                    data-testid={`takeoff-view-${view.id}`}
                    className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors
                      ${takeoffViewMode === view.id ? 'bg-[#0D1830] text-white' : 'text-[#446E87] hover:bg-[#F7FAFB]'}`}
                  >
                    {view.label}
                  </button>
                ))}
              </div>
            )}
            {(divisions.length > 0 || (summary.total_divisions > 0)) ? (
              takeoffViewMode === 'summary' ? (
              <div className="space-y-4">
                {/* Summary cards */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  {[
                    { label: 'Divisions', value: summary.total_divisions, icon: Layers },
                    { label: 'Assemblies', value: summary.total_assemblies, icon: BookOpen },
                    { label: 'Components', value: summary.total_components, icon: BarChart3 },
                    { label: 'Est. Cost', value: fmtCurrency(summary.total_estimated_cost), icon: DollarSign, gold: true },
                  ].map((s, i) => (
                    <Card key={i} className={`border-[#E2E8ED] shadow-none ${s.gold ? 'border-[#1D4053]/20 bg-[#72A4BF]/10' : ''}`}>
                      <CardContent className="p-4 flex items-center gap-3">
                        <div className="w-9 h-9 rounded-xl bg-[#72A4BF]/10 flex items-center justify-center"><s.icon className="w-4 h-4 text-[#0D1830]" /></div>
                        <div><p className={`text-lg font-bold ${s.gold ? 'gold-text' : 'text-[#0D1830]'}`}>{s.value || 0}</p><p className="text-xs text-[#72A4BF]">{s.label}</p></div>
                      </CardContent>
                    </Card>
                  ))}
                </div>

                {/* Analysis mode indicator */}
                {takeoff?.analysis_mode && (
                  <div className="flex items-center gap-2 text-xs text-[#72A4BF]" data-testid="analysis-mode-badge">
                    {takeoff.analysis_mode === 'multimodal_vision' ? (
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 font-medium">
                        <Eye className="w-3 h-3" /> Vision Analysis
                        {takeoff.pages_with_images > 0 && <span className="text-emerald-500">({takeoff.pages_with_images} pages scanned)</span>}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gray-50 text-gray-600 border border-gray-200 font-medium">
                        <FileText className="w-3 h-3" /> Text-Only Analysis
                      </span>
                    )}
                    {takeoff.scale_info?.detected_scales?.length > 0 && (
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-blue-50 text-blue-600 border border-blue-200 font-medium">
                        <Ruler className="w-3 h-3" /> Scale: {takeoff.scale_info.detected_scales[0]}
                      </span>
                    )}
                  </div>
                )}

                {/* View Toggle & File Filter */}
                <Card className="border-[#E2E8ED] shadow-none">
                  <CardContent className="p-3 flex items-center justify-between flex-wrap gap-3">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-[#72A4BF] font-medium">View by:</span>
                      <div className="flex rounded-lg bg-[#F0F4F7] p-0.5">
                        <button 
                          onClick={() => setViewMode('division')}
                          className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${viewMode === 'division' ? 'bg-white text-[#0D1830] shadow-sm' : 'text-[#446E87] hover:text-[#0D1830]'}`}
                          data-testid="view-by-division"
                        >
                          <Layers className="w-3 h-3 inline mr-1" />Division
                        </button>
                        <button 
                          onClick={() => setViewMode('file')}
                          className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${viewMode === 'file' ? 'bg-white text-[#0D1830] shadow-sm' : 'text-[#446E87] hover:text-[#0D1830]'}`}
                          data-testid="view-by-file"
                        >
                          <FileText className="w-3 h-3 inline mr-1" />Source File
                        </button>
                      </div>
                    </div>
                    {viewMode === 'file' && sourceFiles.length > 0 && (
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-[#72A4BF] font-medium">Filter:</span>
                        <select 
                          value={selectedFile}
                          onChange={(e) => setSelectedFile(e.target.value)}
                          className="text-xs border border-[#E2E8ED] rounded-lg px-2 py-1.5 bg-white focus:outline-none focus:border-[#1D4053]"
                          data-testid="file-filter-select"
                        >
                          <option value="all">All Files ({sourceFiles.length})</option>
                          {sourceFiles.map((f, i) => (
                            <option key={i} value={f}>{f} ({fileStats[f]?.component_count || 0} items)</option>
                          ))}
                        </select>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Division View */}
                {viewMode === 'division' && divisions.length > 0 && divisions.map((d, di) => {
                  const dc = (d.assemblies || []).reduce((sum, a) => sum + (a.components || []).reduce((s, c) => s + (c.total_cost || 0), 0), 0);
                  return (
                    <Card key={di} className="border-[#E2E8ED] shadow-none overflow-hidden" data-testid={`div-card-${di}`}>
                      <CardHeader className="pb-2 bg-[#F7FAFB]">
                        <div className="flex items-center justify-between">
                          <CardTitle className="text-sm font-semibold flex items-center gap-2">
                            <span className="w-7 h-7 rounded-lg bg-[#0D1830] text-white text-xs flex items-center justify-center font-bold">{d.division_code || di + 1}</span>
                            {d.division_name}
                          </CardTitle>
                          <span className="text-sm font-semibold gold-text">{fmtCurrency(dc)}</span>
                        </div>
                      </CardHeader>
                      <CardContent className="pt-3">
                        {(d.assemblies || []).map((a, ai) => (
                          <div key={ai} className="mb-3 last:mb-0">
                            <p className="text-xs font-semibold text-[#446E87] mb-2 uppercase tracking-wide">{a.assembly_name}</p>
                            <Table>
                              <TableHeader>
                                <TableRow className="border-[#E2E8ED]">
                                  <TableHead className="text-xs">Component</TableHead>
                                  <TableHead className="text-xs text-right">Qty</TableHead>
                                  <TableHead className="text-xs text-right">Unit</TableHead>
                                  <TableHead className="text-xs text-right">Unit Cost</TableHead>
                                  <TableHead className="text-xs text-right">Total</TableHead>
                                  <TableHead className="text-xs">Confidence</TableHead>
                                  <TableHead className="text-xs">Source</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {(a.components || []).map((c, ci) => (
                                  <TableRow key={ci} className="border-[#F0F4F7]">
                                    <TableCell className="text-xs font-medium">{c.component_name}</TableCell>
                                    <TableCell className="text-xs text-right">{c.quantity}</TableCell>
                                    <TableCell className="text-xs text-right text-[#72A4BF]">{c.unit}</TableCell>
                                    <TableCell className="text-xs text-right">{fmtCurrency(c.unit_cost)}</TableCell>
                                    <TableCell className="text-xs text-right font-medium">{fmtCurrency(c.total_cost)}</TableCell>
                                    <TableCell className="text-xs">
                                      {c.confidence != null && (
                                        <div className="flex items-center gap-1">
                                          <div className="w-10 h-1.5 rounded-full bg-[#EEE] overflow-hidden">
                                            <div className={`h-full rounded-full ${c.confidence >= 0.8 ? 'bg-emerald-500' : c.confidence >= 0.5 ? 'bg-[#1D4053]' : 'bg-red-400'}`}
                                              style={{ width: `${(c.confidence || 0) * 100}%` }} />
                                          </div>
                                          <span className="text-[10px] text-[#72A4BF]">{((c.confidence || 0) * 100).toFixed(0)}%</span>
                                        </div>
                                      )}
                                    </TableCell>
                                    <TableCell className="text-xs">
                                      {(c.source_file || c.page_reference) ? (
                                        <div className="flex flex-col">
                                          {c.source_file && (
                                            <Badge className="bg-[#72A4BF]/10 text-[#0D1830] border-0 text-[9px] px-1.5 py-0 rounded-full whitespace-nowrap mb-0.5">
                                              <FileText className="w-2 h-2 mr-0.5" />{c.source_file.replace('.pdf', '').substring(0, 15)}
                                            </Badge>
                                          )}
                                          <button
                                            className="text-[10px] text-[#0D1830] hover:underline flex items-center gap-0.5 cursor-pointer"
                                            onClick={() => handleViewOnDrawing(c.source_file, c.page_reference)}
                                            data-testid={`view-drawing-btn-${di}-${ci}`}
                                          >
                                            <MapPin className="w-2.5 h-2.5" />{c.page_reference || 'View'}
                                          </button>
                                        </div>
                                      ) : '-'}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </div>
                        ))}
                      </CardContent>
                    </Card>
                  );
                })}

                {/* Fallback when summary has data but divisions array is empty */}
                {viewMode === 'division' && divisions.length === 0 && summary.total_divisions > 0 && (
                  <Card className="border-[#E2E8ED] shadow-none">
                    <CardContent className="p-6 text-center">
                      <AlertCircle className="w-6 h-6 text-[#72A4BF] mx-auto mb-2" />
                      <p className="text-sm font-medium text-[#0D1830]">Division details not available</p>
                      <p className="text-xs text-[#72A4BF] mt-1">The analysis summary was generated but detailed division data may need to be re-analyzed. Try running the analysis again.</p>
                    </CardContent>
                  </Card>
                )}

                {/* File View */}
                {viewMode === 'file' && fileGroupedData && (
                  <div className="space-y-4">
                    {/* File summary cards */}
                    {selectedFile === 'all' && fileGroupedData.files && (
                      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                        {fileGroupedData.files.map((f, i) => (
                          <Card 
                            key={i} 
                            className={`border-[#E2E8ED] shadow-none cursor-pointer hover:border-[#1D4053]/50 transition-colors ${selectedFile === f.filename ? 'border-[#1D4053] bg-[#72A4BF]/10' : ''}`}
                            onClick={() => setSelectedFile(f.filename)}
                            data-testid={`file-card-${i}`}
                          >
                            <CardContent className="p-3">
                              <div className="flex items-start gap-2">
                                <div className="w-8 h-8 rounded-lg bg-[#72A4BF]/10 flex items-center justify-center shrink-0">
                                  <FileText className="w-4 h-4 text-[#0D1830]" />
                                </div>
                                <div className="min-w-0 flex-1">
                                  <p className="text-xs font-semibold text-[#0D1830] truncate" title={f.filename}>{f.filename}</p>
                                  <p className="text-[10px] text-[#72A4BF]">{f.component_count} components</p>
                                  <p className="text-xs font-semibold gold-text">{fmtCurrency(f.total_cost)}</p>
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    )}

                    {/* File-grouped divisions */}
                    {fileGroupedData.files?.filter(f => selectedFile === 'all' || f.filename === selectedFile).map((fileData, fi) => (
                      <Card key={fi} className="border-[#E2E8ED] shadow-none overflow-hidden" data-testid={`file-group-${fi}`}>
                        <CardHeader className="pb-2 bg-gradient-to-r from-[#72A4BF]/10 to-[#F7FAFB]">
                          <div className="flex items-center justify-between">
                            <CardTitle className="text-sm font-semibold flex items-center gap-2">
                              <div className="w-8 h-8 rounded-lg bg-[#1D4053]/20 flex items-center justify-center">
                                <FileText className="w-4 h-4 text-[#0D1830]" />
                              </div>
                              <div>
                                <span className="text-[#0D1830]">{fileData.filename}</span>
                                <p className="text-[10px] text-[#72A4BF] font-normal">{fileData.component_count} components from this file</p>
                              </div>
                            </CardTitle>
                            <span className="text-sm font-semibold gold-text">{fmtCurrency(fileData.total_cost)}</span>
                          </div>
                        </CardHeader>
                        <CardContent className="pt-3">
                          {(fileData.divisions || []).map((d, di) => (
                            <div key={di} className="mb-4 last:mb-0">
                              <div className="flex items-center gap-2 mb-2">
                                <span className="w-6 h-6 rounded bg-[#0D1830] text-white text-[10px] flex items-center justify-center font-bold">{d.division_code}</span>
                                <span className="text-xs font-semibold text-[#0D1830]">{d.division_name}</span>
                              </div>
                              {(d.assemblies || []).map((a, ai) => (
                                <div key={ai} className="ml-8 mb-3 last:mb-0">
                                  <p className="text-[10px] font-semibold text-[#446E87] mb-1 uppercase tracking-wide">{a.assembly_name}</p>
                                  <Table>
                                    <TableHeader>
                                      <TableRow className="border-[#E2E8ED]">
                                        <TableHead className="text-[10px]">Component</TableHead>
                                        <TableHead className="text-[10px] text-right">Qty</TableHead>
                                        <TableHead className="text-[10px] text-right">Unit</TableHead>
                                        <TableHead className="text-[10px] text-right">Total</TableHead>
                                        <TableHead className="text-[10px]">Confidence</TableHead>
                                        <TableHead className="text-[10px]">Page</TableHead>
                                      </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                      {(a.components || []).map((c, ci) => (
                                        <TableRow key={ci} className="border-[#F0F4F7]">
                                          <TableCell className="text-[10px] font-medium">{c.component_name}</TableCell>
                                          <TableCell className="text-[10px] text-right">{c.quantity} {c.unit}</TableCell>
                                          <TableCell className="text-[10px] text-right text-[#72A4BF]">{fmtCurrency(c.unit_cost)}</TableCell>
                                          <TableCell className="text-[10px] text-right font-medium">{fmtCurrency(c.total_cost)}</TableCell>
                                          <TableCell className="text-[10px]">
                                            {c.confidence != null && (
                                              <div className="flex items-center gap-1">
                                                <div className="w-8 h-1 rounded-full bg-[#EEE] overflow-hidden">
                                                  <div className={`h-full rounded-full ${c.confidence >= 0.8 ? 'bg-emerald-500' : c.confidence >= 0.5 ? 'bg-[#1D4053]' : 'bg-red-400'}`}
                                                    style={{ width: `${(c.confidence || 0) * 100}%` }} />
                                                </div>
                                                <span className="text-[9px] text-[#72A4BF]">{((c.confidence || 0) * 100).toFixed(0)}%</span>
                                              </div>
                                            )}
                                          </TableCell>
                                          <TableCell className="text-[10px] text-[#72A4BF]">{c.page_reference || '-'}</TableCell>
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                </div>
                              ))}
                            </div>
                          ))}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
              ) : (() => {
                const rows = [];
              divisions.forEach((d, di) => {
                (d.assemblies || []).forEach((a, ai) => {
                  (a.components || []).forEach((c, ci) => {
                    rows.push({
                      key: `${di}|${ai}|${ci}`,
                      project: project?.name || project?.project_id || '-',
                      assembly: a.assembly_name || d.division_name || '-',
                      floor: c.floor || c.location?.floor || '-',
                      room: c.room || c.location?.room || '-',
                      item: c.component_name || '-',
                      qty: c.quantity,
                      uom: c.unit || '-',
                      confidence: c.confidence,
                      price: c.total_cost,
                      unitCost: c.unit_cost,
                      source: c.source_file || c.page_reference || '-',
                    });
                  });
                });
              });
              return (
                <Card className="border-[#E2E8ED] shadow-none overflow-hidden">
                  <CardHeader className="pb-2 pt-4 px-5 bg-[#F7FAFB] border-b border-[#E2E8ED]">
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="text-sm font-bold text-[#0D1830]">Detailed Takeoff</CardTitle>
                        <p className="text-xs text-[#72A4BF] mt-0.5">{rows.length} line items across {divisions.length} divisions</p>
                      </div>
                      <Badge className="bg-[#0D1830] text-white text-[10px] border-0 rounded-full px-3">{rows.length} items</Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="p-0">
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow className="border-[#E2E8ED] bg-[#F7FAFB] hover:bg-[#F7FAFB]">
                            <TableHead className="px-3 py-3 w-8">
                              <input
                                type="checkbox"
                                aria-label="Select all visible items"
                                checked={rows.length > 0 && rows.every(r => selectedTakeoffItems.has(r.key))}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedTakeoffItems(new Set(rows.map(r => r.key)));
                                  } else {
                                    setSelectedTakeoffItems(new Set());
                                  }
                                }}
                                data-testid="takeoff-select-all"
                              />
                            </TableHead>
                            <TableHead className="text-[10px] uppercase tracking-wider text-[#72A4BF] font-semibold whitespace-nowrap px-4 py-3">Project</TableHead>
                            <TableHead className="text-[10px] uppercase tracking-wider text-[#72A4BF] font-semibold whitespace-nowrap px-4 py-3">Assembly</TableHead>
                            <TableHead className="text-[10px] uppercase tracking-wider text-[#72A4BF] font-semibold whitespace-nowrap px-4 py-3">Floor</TableHead>
                            <TableHead className="text-[10px] uppercase tracking-wider text-[#72A4BF] font-semibold whitespace-nowrap px-4 py-3">Room</TableHead>
                            <TableHead className="text-[10px] uppercase tracking-wider text-[#72A4BF] font-semibold whitespace-nowrap px-4 py-3 min-w-[220px]">Item</TableHead>
                            <TableHead className="text-[10px] uppercase tracking-wider text-[#72A4BF] font-semibold whitespace-nowrap px-4 py-3 text-right">QTY</TableHead>
                            <TableHead className="text-[10px] uppercase tracking-wider text-[#72A4BF] font-semibold whitespace-nowrap px-4 py-3">UOM</TableHead>
                            <TableHead className="text-[10px] uppercase tracking-wider text-[#72A4BF] font-semibold whitespace-nowrap px-4 py-3 text-center">Confidence</TableHead>
                            <TableHead className="text-[10px] uppercase tracking-wider text-[#72A4BF] font-semibold whitespace-nowrap px-4 py-3 text-right">Price/Cost</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {rows.map((r, ri) => (
                            <TableRow key={r.key} className={`border-[#F0F4F7] hover:bg-[#72A4BF]/5 transition-colors ${selectedTakeoffItems.has(r.key) ? 'bg-[#0D1830]/5' : ''}`} data-testid={`detailed-row-${ri}`}>
                              <TableCell className="px-3 py-2.5 w-8">
                                <input
                                  type="checkbox"
                                  checked={selectedTakeoffItems.has(r.key)}
                                  onChange={() => toggleTakeoffItem(r.key)}
                                  data-testid={`takeoff-checkbox-${ri}`}
                                  aria-label={`Select ${r.item}`}
                                />
                              </TableCell>
                              <TableCell className="text-xs text-[#446E87] px-4 py-2.5 whitespace-nowrap">{r.project}</TableCell>
                              <TableCell className="px-4 py-2.5">
                                <span className="inline-flex items-center justify-center px-2 py-0.5 rounded bg-[#0D1830]/5 text-[10px] font-bold text-[#0D1830] tracking-wide">
                                  {r.assembly}
                                </span>
                              </TableCell>
                              <TableCell className="text-xs text-[#446E87] px-4 py-2.5">{r.floor}</TableCell>
                              <TableCell className="text-xs text-[#446E87] px-4 py-2.5">{r.room}</TableCell>
                              <TableCell className="text-xs font-medium text-[#0D1830] px-4 py-2.5">{r.item}</TableCell>
                              <TableCell className="text-xs font-semibold text-[#0D1830] px-4 py-2.5 text-right tabular-nums">{r.qty != null ? r.qty : '-'}</TableCell>
                              <TableCell className="text-[10px] text-[#72A4BF] px-4 py-2.5 uppercase font-medium">{r.uom}</TableCell>
                              <TableCell className="px-4 py-2.5 text-center">
                                {r.confidence != null ? (
                                  <div className="flex items-center justify-center gap-1.5">
                                    <div className="w-10 h-1.5 rounded-full bg-[#E2E8ED] overflow-hidden">
                                      <div
                                        className={`h-full rounded-full ${r.confidence >= 0.8 ? 'bg-emerald-500' : r.confidence >= 0.5 ? 'bg-amber-400' : 'bg-red-400'}`}
                                        style={{ width: `${Math.round(r.confidence * 100)}%` }}
                                      />
                                    </div>
                                    <span className="text-[10px] text-[#72A4BF] tabular-nums">{Math.round(r.confidence * 100)}%</span>
                                  </div>
                                ) : <span className="text-[10px] text-[#72A4BF]">-</span>}
                              </TableCell>
                              <TableCell className="text-xs font-semibold text-[#0D1830] px-4 py-2.5 text-right tabular-nums">{r.price != null ? fmtCurrency(r.price) : '-'}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                    {/* Summary row */}
                    <div className="border-t-2 border-[#0D1830]/10 bg-[#F7FAFB] px-4 py-3 flex items-center justify-between">
                      <span className="text-xs font-semibold text-[#0D1830]">Total ({rows.length} items)</span>
                      <span className="text-sm font-bold text-[#0D1830]">{fmtCurrency(rows.reduce((s, r) => s + (r.price || 0), 0))}</span>
                    </div>
                  </CardContent>
                </Card>
              );
            })()
            ) : (
              <Card className="border-dashed border-2 border-[#E2E8ED] shadow-none">
                <CardContent className="py-16 text-center">
                  <BarChart3 className="w-10 h-10 text-[#1D4053]/30 mx-auto mb-3" />
                  <p className="text-sm text-[#72A4BF]">No takeoff data yet. Upload documents and run AI analysis.</p>
                </CardContent>
              </Card>
            )}
            {/* Floating action bar for multi-select Add to Quote (Pass #16) */}
            {selectedTakeoffItems.size > 0 && (
              <div
                className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 bg-[#0D1830] text-white rounded-full shadow-lg px-4 py-2 flex items-center gap-3"
                data-testid="takeoff-action-bar"
              >
                <span className="text-xs font-semibold">
                  {selectedTakeoffItems.size} item{selectedTakeoffItems.size !== 1 ? 's' : ''} selected
                </span>
                <button
                  onClick={clearTakeoffSelection}
                  className="text-[10px] font-medium text-white/70 hover:text-white px-2 py-1 rounded"
                  data-testid="takeoff-clear-selection"
                >
                  Clear
                </button>
                <Button
                  size="sm"
                  onClick={openAddToQuote}
                  className="bg-white text-[#0D1830] rounded-full text-xs font-semibold hover:bg-[#F7FAFB]"
                  data-testid="takeoff-add-to-quote-btn"
                >
                  <Plus className="w-3 h-3 mr-1" />Add to Quote
                </Button>
              </div>
            )}
          </TabsContent>

          {/* QUOTES TAB — list view per UIDESIGN1 page 2 */}
          <TabsContent value="quotes" data-testid="quotes-tab">
            <QuotesListPanel projectId={projectId} project={project} />
          </TabsContent>
        </Tabs>
      </main>

      {/*
        Floating AI chat widget — visible on every tab except Workspace.
        Click the FAB → expands into a 400×600 chat panel anchored bottom-right.
        Reuses the existing AIAssistant component. No backdrop — underlying
        tab content stays interactive while the widget is open.
      */}
      {activeTab !== 'workspace' && (
        <>
          {/* Floating Action Button */}
          {!chatWidgetOpen && (
            <button
              type="button"
              onClick={() => setChatWidgetOpen(true)}
              data-testid="chat-fab"
              title="Ask Nivela AI"
              className="fixed bottom-6 right-6 z-30 group flex items-center gap-2 bg-[#0D1830] hover:bg-[#1D4053] text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-105 px-4 py-3"
              style={{ originY: 1, originX: 1 }}
            >
              <MessageSquare className="w-5 h-5" />
              <span className="text-xs font-semibold pr-1 hidden sm:inline">Ask AI</span>
            </button>
          )}

          {/* Chat widget panel */}
          {chatWidgetOpen && (
            <div
              data-testid="chat-widget"
              role="dialog"
              aria-label="Nivela AI assistant"
              className="fixed bottom-6 right-6 z-30 w-full sm:w-[400px] bg-white border border-[#E2E8ED] rounded-2xl shadow-2xl flex flex-col origin-bottom-right animate-[fadeIn_200ms_ease-out]"
              style={{
                height: 'min(80vh, 600px)',
                maxWidth: 'calc(100vw - 24px)',
              }}
            >
              {/* Header */}
              <div className="h-11 px-4 flex items-center justify-between border-b border-[#E2E8ED] bg-[#F7FAFB] rounded-t-2xl shrink-0">
                <div className="min-w-0 flex-1">
                  <div className="text-xs font-bold text-[#0D1830] leading-tight">Ask Nivela AI</div>
                  {chatContextLabel && (
                    <div className="text-[10px] text-[#72A4BF] leading-tight truncate">
                      {chatContextLabel}
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => setChatWidgetOpen(false)}
                  className="p-1 rounded-md text-[#72A4BF] hover:text-[#0D1830] hover:bg-[#0D1830]/5 transition-colors shrink-0"
                  data-testid="chat-widget-close"
                  title="Close (Esc)"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              {/* Body — reuses existing AIAssistant */}
              <div className="flex-1 overflow-hidden">
                <AIAssistant projectId={projectId} quoteId={quoteId} onQuoteUpdate={handleQuoteUpdate} />
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
